import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Chip, Button, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel,
  Dialog, DialogTitle, DialogContent, IconButton, Link, Divider, List, ListItem, ListItemText, Grid, Stack,
  Alert, useTheme, useMediaQuery,
} from '@mui/material';
import ProductThumb from './common/ProductThumb';
import BottomSheetSelect from './common/BottomSheetSelect';
import BottomSheetMultiSelect from './common/BottomSheetMultiSelect';
import SlidePanel from './common/SlidePanel';
import BundleComponentList, {
  bundleVariantsDiffer, describeBundleCost,
} from './products/BundleComponentList';
import BundleCostProvenance from './products/BundleCostProvenance';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import TableFooterSimple from './common/TableFooterSimple';
import FilterPillRow from './common/FilterPillRow';
import ListToolbar from './common/ListToolbar';
import useListQuery from './common/listQuery/useListQuery';
import Pill from './common/Pill';
import EmptyState from './common/EmptyState';
import ProductsTableSkeleton from '../Skeletons/ProductsTableSkeleton';
import TableViewport from './common/TableViewport';
import MobileRecordCard from './common/MobileRecordCard';
import useContainerWidth from './common/useContainerWidth';
import useColumnPriority from './common/useColumnPriority';
import { getCanonicalTypeDisplayName } from '../utils/canonicalTypes';
import { productStatusInfo } from '../utils/productStatus';
import Panel from './common/Panel';
import RefreshButton from './common/RefreshButton';
import { SP, COLUMN_MAX, CONTENT_WIDTH, TOUCH_TARGET_COARSE, DICHTER_TEXT_LINK_SX } from '../theme/tokens';
import { v2Tone } from '../theme/theme';
import { margeProzentVomNetto } from '../utils/margen';
import { formatDate, formatMoney, formatNumber, formatPercent } from '../utils/format';
import { marketLabel } from '../utils/market';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const OVERVIEW_URL = `${API_BASE}/api/product_overview`;
// 🔴 NUR die Tabelle sendet den Parameter (Regel 1 §2b). Das Portfolio-Brett
// (`ProductPortfolioDashboard.jsx`) fährt einen ZWEITEN Abruf derselben Route
// und rechnet seine KPIs über die archived-freie Menge — es bleibt bei
// `OVERVIEW_URL` und damit zahlenstabil.
const OVERVIEW_URL_TABLE = `${OVERVIEW_URL}?include_archived=1`;
const RECS_URL     = `${API_BASE}/api/products/recommendations_summary?has_reco=true&page_size=5000`;

/** 🔴 D-3a (Plan S7b §6.4): der EINE Leerwert der Vertical-Facette.
 *
 *  Ein Produkt ohne Zeile in `product_vertical_assignment` traegt `vertical_key
 *  = null` — und „unbekannt ist nie `0` und nie leer" (Regel 22 §3). Die
 *  Namenszelle, die Spalte und die mobile Karte zeigen deshalb den Text
 *  „not set"; die FACETTE braucht dafuer einen adressierbaren Wert, sonst ist
 *  die Menge ueber den Filter nicht erreichbar (der Leerzustand waere nur zu
 *  sehen, wenn man die Liste ohne Filter durchblaettert).
 *
 *  Der Wert ist KEIN Vertical-Key (die Schluessel sind Slugs). Gefiltert wird
 *  CLIENTSEITIG, wie bei `status`/`category`/`type` — der Endpunkt kennt keinen
 *  `?vertical=`-Parameter (R15 B2: er hatte keinen Aufrufer, und zwei
 *  Implementierungen desselben Kriteriums koennen driften).
 *  `backend/tests/test_product_overview_vertical_join.py` pinnt, dass `__none__`
 *  kein echter Schluessel in `product_verticals` ist. */
const VERTICAL_NONE = '__none__';
/** Der sichtbare Text des Leerzustands — an DREI Stellen dieselbe Quelle
 *  (Spalte, Namenszelle, Facetten-Option), kein zweites Literal.
 *  🔴 **„not set", wortgleich mit dem Produktdetail** (`ProductDetail.jsx:2634`
 *  › `Vertical: not set`): EIN Wort fuer denselben Zustand, nicht „ohne
 *  Zuordnung" hier und „not set" dort (R16 C2). Die Liste ist englisch
 *  („Product", „On hand", „Any") — „not set" ist auch deshalb das Hauswort. */
const VERTICAL_NONE_LABEL = 'not set';
const ACTIVE_STATUSES = ['active', 'draft'];
// Alle vier Shopify-Status, EINE Quelle für die Auswahl auf beiden Breiten
// (`FilterSheet`-Blatt und `BottomSheetMultiSelect`-Pille) — zwei getippte
// Listen sind genau die Sorte Doppelung, die auseinanderläuft.
//
// 🔴 **Vier Werte, und das ist gemessen, nicht angenommen (G1a, 2026-10-14).**
// Der Plan `2026-10-13-ui2-t10-g1-produkte.md` §2.5 wollte `archived` aus der
// Facette nehmen — mit der Begründung, `/api/product_overview` schliesse
// archivierte Zeilen aus (`products_api.py` `!= 'archived'`), die Option treffe
// also nie. **Das gilt für den VORGABE-Abruf, nicht für diesen Aufrufer:**
// `OVERVIEW_URL_TABLE` (s. o.) sendet `include_archived=1`, und die WHERE-Zeile
// lautet `WHERE (:include_archived OR COALESCE(p.product_status,'draft') !=
// 'archived')` (`backend/routes/products_api.py:575`) — mit dem Parameter
// kommen die archivierten Zeilen MIT. Das war Absicht: derselbe Commit, der den
// Pill „In stock, hidden" baute (`d1c4621ba0`, 2026-10-13 22:47, auf `master`),
// machte „Status überall erreichbar" und setzte `unlisted` UND `archived` in
// diese Liste. `archived` fällt damit NICHT heraus — es fällt heraus, was
// niemand erreichen kann, und das ist hier nichts.
const STATUS_OPTIONS = ['active', 'draft', 'unlisted', 'archived'];
// „In Shopify nicht sichtbar" — die Status, die der neue Pill meint.
const HIDDEN_STATUSES = ['draft', 'unlisted', 'archived'];

// Die fünf Quick-Filter (§2.3, EINE Quelle für Z2 UND das Blatt). 🔴 **Fünf,
// nicht vier:** der Plan nennt vier (§1.2 `:507-527`), der Kopf trägt seit
// `d1c4621ba0` aber `in_stock_hidden` als fünften — die Zeile ist gemessen, und
// eine Pille, die im Kode steht und im Plan fehlt, wird nicht stillschweigend
// entfernt (§1.2 „kein stiller Verlust", in der Gegenrichtung).
const QUICK_OPTIONS = [
  { value: 'with_recs', label: 'Needs price action' },
  { value: 'stockouts', label: 'Stock-out ≤14d' },
  { value: 'low_margin', label: 'Low margin' },
  { value: 'in_stock', label: 'In stock' },
  {
    // Der sichtbare Text bleibt kurz (die Reihe trägt schon fünf Pillen); die
    // Langform steht im Tooltip und im zugänglichen Namen — MIT dem Vorbehalt,
    // den das Prädikat wirklich hält (Grill W2): „active, aber nicht im
    // Online-Store veröffentlicht" ist NICHT enthalten, weil Forge die
    // Veröffentlichung nicht speichert.
    value: 'in_stock_hidden',
    label: 'In stock, hidden',
    // Label, `ariaLabel` und `title` tragen denselben Anfang („In stock,
    // hidden") — R16 W3: der sichtbare Hover-Text darf nicht mit einem anderen
    // Wort beginnen als die Beschriftung.
    ariaLabel:
      'In stock, hidden — not visible in Shopify (draft, unlisted, archived). '
      + 'Active products that are not published to the Online Store are not included.',
    title:
      'In stock, hidden — not visible in Shopify (draft, unlisted, archived). '
      + 'Active products that are not published to the Online Store are not included.',
  },
];

// Die **neun** sortierbaren Spalten (§4.2). 🔴 `vertical` stand hier kurz mit
// D-3a — die Spalte ist am 2026-11-04 wieder entfallen (Regel-8-Befund: sie
// sprengte ab ~2400 px das Breitenbudget, PW4b/PW5b/PW7); ohne Spalte gibt es
// keinen Sortierkopf, also auch keinen Sortierwert. Der Wert selbst bleibt in
// `processedRows` — die Ausnahme-Marke und der Filter lesen ihn.
// Der URL-Wert ist `feld` (aufsteigend) oder `-feld` (absteigend) —
// `useListQuery` §`sort`. 🔴 **Die Liste traegt neun Felder, und die neun
// stimmen** (Gegenprobe: `SORT_FELDER.length` gegen den Kopf; die `xl`-Spalte
// `vertical` ist am 2026-11-04 entfallen). Der Satz, der hier stand („Die Zahl
// stand hier bis zum 2026-11-04 als neun"), warnte gegen die eigene, richtige
// Zahl — er ist ersatzlos weg.
// 🔴 **G1c-2: `brand` ist der Wert aus G1c-2, und ohne ihn ist der Klick nicht nur
// wirkungslos, sondern SICHTBAR falsch** (§4.3 Punkt 5): `schreibeAchse` prueft
// den Wert nicht, der naechste Render liest `?sort=brand` gegen diese Liste und
// VERWIRFT ihn — Rueckfall auf `product_name` plus Hinweiszeile
// (`ignoriert-hinweis`). Der Eintrag ist also die erste von drei Sortierzielen
// (§2.3); die zwei anderen sind der String-Zweig des Vergleichers und der
// Sortierkopf. 🔴 Der Blatt-Sortierabschnitt (unten) bleibt bei DREI Schluesseln
// — Brand sortiert ueber den Tabellenkopf, und mobil gibt es keinen.
const SORT_FELDER = [
  'product_name', 'brand', 'category_abcd', 'current_inventory',
  'stock_out_date', 'list_price', 'margin_pct', 'average_sales_30d', 'rec_abs_diff',
];

// 🔴 **Die URL-Werte sind BEIDE Richtungen — und das ist nicht Kosmetik
// (D-3a-Nachbesserung, 2026-11-04).** `useListQuery.leseAchse` prueft den
// ROHEN Wert gegen diese Liste (`useListQuery.js:469-475`): steht `-feld`
// nicht darin, verwirft es den Wert, meldet ihn im `ignoriert-hinweis` und
// faellt auf die Vorgabe zurueck. Bis hierher standen hier nur die nackten
// Feldnamen — damit war **jede** absteigende Sortierung wirkungslos, obwohl
// der Tabellenkopf `-feld` schreibt (`handleRequestSort`) und das mobile Blatt
// `-rec_abs_diff` anbietet. Gemessen an W2b: `?sort=-product_name` lieferte die
// Vorgabe-Reihenfolge (`product_name` aufsteigend: Brew, Ohne, Peak) statt
// Peak, Brew, Ohne. *Widerlegt haette es:* ein `aria-sort="descending"` am
// Kopf bei gleichzeitig falscher Zeilenfolge — dann waere der Wert gelesen
// worden und der Fehler im Vergleicher zu suchen.
const SORT_WERTE = SORT_FELDER.flatMap((feld) => [feld, `-${feld}`]);

// Eine Modulkonstante für den leeren Fall — s. `statusFilter` im Rumpf.
const LEER_ARRAY = [];
// 🔴 `productStatusInfo` liefert MUI-Farbnamen (der Detailseiten-Chip), `Pill`
// spricht Töne. Die Übersetzung steht HIER und nicht in
// `utils/productStatus.js`: die geteilte Ableitung behält ihre eine Form, und
// der Zustand wird als `Pill` gerendert — der Kanon für Status (Regel 22 §1,
// `component-registry.md` „for a NON-glass chip (status, …) use `Pill`").
const statusTone = (farbe) =>
  (farbe === 'success' ? 'ok' : farbe === 'warning' ? 'warn' : 'plain');

// 🔴 EINE Statusform für BEIDE Breiten (R15 W1 = R16 W1). Mobil stand hier bis
// zum 2026-10-13 der rohe, kleingeschriebene Status an JEDER Zeile
// (`sku · draft`, auch bei `active`), am Rechner daneben eine `Pill` — dieselbe
// Grösse in zwei Formen und zwei Schreibweisen. Jetzt liefern beide dasselbe
// Etikett aus `productStatusInfo(r).label` (derselben Quelle wie die
// Detailseite) und beide zeigen es nur, wenn der Status NICHT `active` ist.
// Der Wächter bleibt `!== 'active'` (R16 W4, bewusst getragen): die vier
// Shopify-Status sind active/draft/unlisted/archived, „alles ausser active"
// ist heute äquivalent zu „∈ HIDDEN_STATUSES" und deckt auch `inactive` mit ab.
function StatusPill({ row }) {
  const st = productStatusInfo(row);
  if (!st || row.product_status === 'active') return null;
  return <Pill tone={statusTone(st.color)}>{st.label}</Pill>;
}

// 🔴 **D-3a (R16 C1): die Vertikale als AUSNAHME-Marke, nicht als Zeile auf
// jeder Breite.** 281 von 306 Zeilen tragen `brewout` (Plan §6.1: 281 brewout /
// 18 peakblades / 7 ohne) — eine 320-px-Spalte fuer eine 92-%-Konstante ist
// teuer. 🔴 **Seit dem 2026-11-04 ist die `xl`-Spalte GANZ entfallen** (Regel 8:
// PW4b/PW5b/PW7, Breitenbudget ab ~2400 px) — nicht nur „unterhalb von xl nicht
// im DOM". Hier stand das Gegenteil, sieben Zeilen über dem Absatz, der den
// Entfall beschreibt.
// Markiert wird deshalb nur, was ABWEICHT: jede Vertikale ausser `brewout`
// (heute `peakblades`, Beschriftung aus `vertical_label`) und der Leerzustand
// „not set". `brewout` bleibt unmarkiert.
// Hausmuster ist `StatusPill` direkt darueber: „beide Breiten, eine Form" —
// dieselbe `Pill` (ohne `onClick`, also ein `<span>`, kein Bedienelement)
// steht in der Namenszelle der Tabelle UND in den `chips` der mobilen Karte.
// 🔴 **Die Marke ist seit dem 2026-11-04 die EINZIGE Anzeige** — die `xl`-Spalte
// ist entfallen (Regel 8: sie sprengte ab ~2400 px das Breitenbudget,
// PW4b/PW5b/PW7). Der Filter und die mobile Karte tragen den Wert daneben.
// Der Text kommt aus `r.vertical` (Label → Schluessel → „not set", die EINE
// Ableitung in `processedRows`).
// 🔴 **M2g-Nachbesserung (2026-11-04): die Marke bricht NICHT um und gibt
// keinen Pixel ab.** Im Browser gemessen (Orchestrator, `/products`, 1024,
// grober Zeiger) stand „not set" als **„not / set"** auf zwei Zeilen, 42 px
// hoch: die Namenszeile ist `display: flex` ohne `flexWrap`, die Glieder sind
// Name, Status-Pille und diese Marke — ist die Zeile schmaler als ihre
// Inhaltsbreite, schrumpfen die schrumpffähigen Glieder, und `Pill` schrumpft
// per Vorgabe (`nowrap` ist opt-in, `Pill.jsx` §`nowrap`). Eine Ausnahme-Marke
// ist ein Kennzeichen, kein Satz: `nowrap` hält den Text einzeilig,
// `flexShrink: 0` nimmt ihr die Schrumpfung — die Zeile löst den Engpass dann
// am NAMEN (der klemmt bei zwei Zeilen, `TITEL_KLEMME_SX`), nicht am Etikett.
// *Widerlegt hätte es:* eine rote Zeile, in der die Marke schon einzeilig war
// (dann wäre die Ursache woanders) — die Messung zeigt sie zweizeilig.
const VERTICAL_MARK_SX = { flexShrink: 0, whiteSpace: 'nowrap' };

function VerticalMark({ row }) {
  if (row.vertical_key === 'brewout') return null;
  return <Pill tone="plain" sx={VERTICAL_MARK_SX}>{row.vertical}</Pill>;
}
const noCache = (u) => `${u}${u.includes('?') ? '&' : '?'}_=${Date.now()}`;

// Dieselbe Geldform wie überall (PR 2, `formatMoney`): `1.234,56 €`,
// Fremdwährung als Code HINTEN (`1.234,56 USD`). Der frühere `locale`-Parameter
// ist entfallen — kein Aufrufer setzte ihn.
const fmtCurrency = (v, currency = 'EUR') => formatMoney(v, { currency });

const ROWS_PER_PAGE = 50;

/**
 * Die **neun** Spalten mit ihrer **Stufe** und **Art** — G1b, Plan §2.6,
 * Konzept `inhaltsbreite.md` §3.2.2/§3.3; `brand` kam mit G1c-2 dazu.
 * 🔴 `vertical` stand hier kurz mit D-3a und ist am 2026-11-04 wieder entfallen
 * (Regel-8-Befund PW4b/PW5b/PW7: ab ~2400 px sprengte sie das Breitenbudget).
 * Den Wert traegt die Ausnahme-Marke in der Namenszelle (R16 C1), nicht eine
 * Spalte.
 *
 * 🔴 **Der Descriptor traegt `kind`, kein eigenes `min`/`max`** (Grill W12,
 * §3.3:500-504): `min`/`max` leitet `useColumnPriority`/`TableViewport` aus
 * `COLUMN_MAX[kind]` ab — zwei Quellen fuer dieselbe Zahl sind genau der
 * Zustand, den das Konzept abschafft.
 *
 * 🔴 **Die Stufen sind die Startwerte des Konzepts (§3.2.2:445-447), NICHT
 * gemessen** — der Plan §2.6 nennt sie ausdruecklich „Startwerte, nicht
 * gemessen" und verlangt, der Messung zu folgen, wo sie abweicht. Die
 * G0b-Messung hat `lg` bei **1024** gelassen (`tokens.js`), also bleibt es
 * dabei; `kern` = Product · On hand · List, `lg` = Category · Stock-out ·
 * Margin · Price reco, `xl` = 30d Sales.
 *
 * 🔴 **Modulkonstante, kein Literal im Render** — `useColumnPriority` haengt an
 * der Array-IDENTITAET (R15-Warnung 2); ein frisches Literal je Render liesse
 * den `useMemo` bei jedem Durchlauf neu rechnen.
 *
 * ⚠️ `Product` traegt `identity`, **nicht** `text`: die Spalte fuehrt den
 * Produktnamen und ist die Kennungsspalte (sie wird im Rollfall kleben, N9.6).
 *
 * 🔴 **G1c-2: `brand` ist die Spalte aus G1c-2, und ihre STELLE in dieser Liste
 * ist der Fit-Entscheid, nicht die Anzeige.** `brand` und `category_abcd` sind beide
 * `lg`/`text` (`max` 320) und liegen im Fit hinter `Stock-out`/`Margin`/
 * `Price reco` (je 160/160/200); bei der Musterbreite (1280) kommt nur EINE der
 * beiden mit, und `vergleicheOptional` entscheidet bei gleicher Stufe UND
 * gleicher Groesse ueber den **Index** — `brand` steht deshalb VOR
 * `category_abcd`. Angezeigt wird sie ebenfalls an Position 2 (hinter
 * `Product`), im Fit aber an vierter Stelle der Optionalen (Stufe, dann
 * Groesse, dann Index). Gerechnet in Plan §2.3a.
 */
const SPALTEN = [
  { key: 'product_name', priority: 'kern', kind: 'identity' },
  { key: 'brand', priority: 'lg', kind: 'text' },
  { key: 'category_abcd', priority: 'lg', kind: 'text' },
  { key: 'current_inventory', priority: 'kern', kind: 'number' },
  { key: 'stock_out_date', priority: 'lg', kind: 'date' },
  { key: 'list_price', priority: 'kern', kind: 'number' },
  { key: 'margin_pct', priority: 'lg', kind: 'number' },
  { key: 'average_sales_30d', priority: 'xl', kind: 'number' },
  { key: 'rec_abs_diff', priority: 'lg', kind: 'chip' },
];

/** Der Medien-Schluessel des Trefferziel-Bodens — ein Literal JE DATEI.
 *
 *  🔴 **Nicht „EINMAL"** (R16 W3, Nachbesserung 5, 2026-10-15): dieselbe
 *  Zeichenkette steht auch in `rma/RMAFaelligkeit.jsx:443` und
 *  `components/common/SplitButton.jsx:8` — und als Objektschluessel ohnehin an
 *  vielen Stellen (`TOUCH_TARGET_COARSE` in `tokens.js`, `FilterPillRow`,
 *  `SituationHeader`, …). Der frueher hier stehende Satz „EINMAL" war falsch; er
 *  ist durch diese Zeile ersetzt, **nicht** durch eine Heilung — ein geteilter
 *  Export fuer einen Medien-String waere mehr Maschinerie als der Befund wert
 *  ist.
 *
 *  🔴 **EINMAL ist dagegen der WERT:** die Hoehe kommt aus `TOUCH_TARGET_COARSE`
 *  (`{ '@media (pointer: coarse)': { minHeight: 44 } }`, `tokens.js:435`), nicht
 *  als zweites Literal daneben (Hausform, Regel 22 §8). */
const GROBER_ZEIGER = '@media (pointer: coarse)';

/** Die Zeilenklemme eines Produkttitels — **hoechstens zwei Zeilen**, dann
 *  Auslassung; vollstaendig steht er im Detail und per `title`.
 *
 *  🔴 Konzept §9.1 („Mehr als zwei Zeilen gibt es nicht") und N11.5 („der Titel
 *  bricht auf hoechstens zwei Zeilen um und kuerzt dann mit Auslassung"). Seit
 *  der G1b-Nachbesserung 2 (2026-10-14) gilt die Klemme auf BEIDEN Breiten: der
 *  Fotobefund zeigte am Rechner einen **dreizeiligen** Titel (1280 und 1440),
 *  weil der Status-Chip inline daneben Breite nimmt. Kein `nowrap`: ein
 *  Produktname ist kein Kennzeichen, und eine Zeile schnitte ihn nach ~18
 *  Zeichen ab. `overflow: hidden` sitzt am TITEL, nicht an der Kennung — die
 *  traegt `nowrap` und wird nie gekuerzt (N11.5, P15). */
const TITEL_KLEMME_SX = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

/** Der Titel der mobilen Karte (N11.5) — die Karten-Schrift plus der Haus-Boden.
 *
 *  🔴 **Der Titel ist das EINZIGE Bedienelement der Karte (R16 K1, 2026-10-14).**
 *  Der Titel-`Link` ist der einzige Weg in den Datensatz
 *  (`MobileRecordCard` §@dontUseFor: die FLaeche traegt keinen
 *  `onClick`); sein Kasten ist die Textzeile — bei einem einzeiligen Namen
 *  **21 px**, zweizeilig 42 px (1 rem = 14 px, `theme.js` `html { font-size }`;
 *  `body1` = 1 rem bei `lineHeight` 1,5). Haus-Boden ist **44** (N11.19,
 *  Regel 22 §8). 🔴 Bis zur Nachbesserung 6 stand hier „~19–24 px" — eine
 *  Schaetzung; die 21 px sind aus der Theme-Konstante gerechnet und tragen die
 *  Verankerung unten.
 *
 *  🔴 **Der Boden liegt auf DIESEM `Link`, und der `Link` traegt KEIN
 *  `overflow`.** Die Zwei-Zeilen-Klemme (`TITEL_KLEMME_SX`: `-webkit-box` +
 *  `overflow: hidden`) sitzt deshalb auf einem INNEREN `<span>` (Aufrufstelle
 *  unten), nicht hier. 🔴 **Das ist die Bedingung, an der die erste Fassung
 *  scheiterte (R16 B1, 2026-10-15):** ein absolut positionierter `::after` wird
 *  vom `overflow: hidden` seines **Containing Blocks** auf dessen Padding-Box
 *  beschnitten (CSS 2.1 §11.1.1). Lag die Klemme am `Link`, war dieser zugleich
 *  Containing Block **und** Traeger des `overflow` — der 44-px-Boden schrumpfte
 *  auf die Textzeile (21 px), und die Zusage war nur noch im Kommentar wahr.
 *  Mit dem `overflow` am inneren `<span>` ist der Containing Block des Bodens
 *  unbeschitten; die Klemme wirkt weiter, weil sie nur den TEXT kuerzt.
 *
 *  🔴 Dieselbe Bauart wie `rma/RMAFaelligkeit.jsx:497-508`: der Boden liegt als
 *  **FLAECHE** (`::after`, `left/right: 0`, Hoehe aus dem Token), nicht als
 *  `minHeight` am Kasten. Ein `minHeight` am `Link` haette die Titelzeile — und
 *  damit jede Karte — um 44 − 27,5 = 16,5 px gehoben; die Flaeche waechst nach
 *  aussen, ohne am Layout zu ruecken.
 *
 *  🔴 **An der OBERKANTE verankert (top als Theme-Funktion, `-8px` gerendert),
 *  nicht an der Unterkante
 *  (R16 W1 / R15 W1 der Runde 3, Nachbesserung 6, 2026-10-15).** Die Hauslehre
 *  `ViewHeader.test.jsx:345-352` lautet: die Flaeche waechst von der
 *  Nachbarflaeche WEG, nicht in sie hinein. Dort lag die Nachbarflaeche UNTEN
 *  (die Titelzeile); hier liegt sie OBEN — die VORKARTE. Ein an `bottom: 0`
 *  verankerter Boden waechst 44 − 21 = 23 px nach oben; im Karteninneren stehen
 *  ihm davon nur die 8 px Polsterung zur Verfuegung
 *  (`MobileRecordCard` `p: SP.tight`), und der Kartenabstand ist `mb: SP.chip`
 *  = 4 px — der Rest landete auf der Karte darueber. Ein Tipp auf deren unteren
 *  Rand oeffnete damit das FALSCHE Produkt.
 *
 *  Deshalb haengt die OBERKANTE der Flaeche `SP.tight` ueber der Titel-Oberkante
 *  (`top` als Funktion des Themes, aus dem Token, kein px-Literal), und die 44 px
 *  wachsen nach UNTEN ueber den Titel und die Kennungszeile.
 *
 *  🔴 **Zwei Kanten, zwei Namen (R15 W2, Runde 4).** „Innenkante des
 *  Kartenrahmens" (y = **1**, die Innenkante des 1-px-Rahmens) und „Oberkante der
 *  Polsterung" (y = **9**, Padding-Box-Oberkante) sind VERSCHIEDENE Kanten; der
 *  Satz „hoechstens `SP.tight` ueber der inneren Oberkante, also genau an der
 *  Innenkante" las sich als Widerspruch, weil beide Namen dieselbe Kante zu
 *  meinen schienen. Belastbare Form: **die Oberkante der Flaeche bleibt in jeder
 *  Lage ≥ der Rahmen-Innenkante (δ ≥ 0) — also immer in der eigenen Karte.**
 *
 *  Die Geometrie in Kartenkoordinaten: Rahmen 1 px + Polsterung 8 px ⇒
 *  Padding-Box-Oberkante **9**. Titel-Oberkante 9 + (27,5 − 21)/2 = **12,25**;
 *  Flaeche 12,25 − 8 = **4,25** bis **48,25**. Sie liegt damit 4,75 px **ueber**
 *  der Polster-Oberkante (4,25 gegen 9 — y waechst nach unten, kleiner heisst
 *  hoeher) und 3,25 px **unter** der Rahmen-Innenkante — sie liegt in
 *  der Polsterung, nicht an der Rahmenkante, und in JEDEM Fall in der eigenen
 *  Karte. Unguenstigster Fall (kein `actions`): Titel-Oberkante 9 ⇒
 *  Flaechen-Oberkante 1 = genau die Rahmen-Innenkante, δ = 0.
 *
 *  🔴 **Die 27,5 px sind eine Abhaengigkeit, kein Nebenwert (R16 W2, Runde 4).**
 *  Die Kopfzeile ist 27,5 px hoch, weil das hoehere Kind die `Pill` in `actions`
 *  ist (`py: 0.5` + `FS.sm` × 1,4 + Rahmen); der `Link` mit seinen 21 px wird
 *  darin zentriert (`alignItems="center"`). Fiele `actions` weg, waere die
 *  Kopfzeile 21 px, die Titel-Oberkante 9, die Flaeche 1 … 45 — immer noch in der
 *  Karte (δ = 0), aber der Rest unterhalb waere 11 px statt 8. Die Zahl „rund
 *  8 px" haengt also an der `actions`-`Pill`; wer die Kopfzeile umbaut, misst
 *  diesen Rest neu.
 *
 *  🔴 **Was sie bedeckt — und was nicht.** Sie bedeckt den Titel (das
 *  Bedienelement) und das **obere Drittel** der Kennungszeile (einer
 *  `Typography`, KEIN Bedienelement): die Flaechen-Unterkante liegt bei 48,25,
 *  die Kennungszeile beginnt bei 40,5 (Kopfzeile endet 36,5 + `mt: SP.chip` 4)
 *  und ist gerechnet ~20,75 px hoch (Kennung **`FS.sm` 12,5 px**,
 *  `MobileRecordCard.jsx:371` — NICHT die `caption`-Vorgabe, die ist 10,5 px —
 *  × `caption`-`lineHeight` 1,66, den `sx` nicht ueberschreibt) — bedeckt sind davon
 *  48,25 − 40,5 = **7,75 px**, also **~37 %, das obere Drittel**. Bei
 *  zweizeiligem Namen liegt sie ohnehin ganz im Titel.
 *
 *  🔴 **Zwei Lagen, zwei Zahlen — und die erste Fassung nannte nur eine
 *  (R16 W1, Runde 5).** Hier stand „bei einzeiligem Namen endet sie rund 8 px
 *  VOR der Chip-Zeile"; das war die Rechnung der Fassung VOR der Oberkanten-
 *  Verankerung (Flaeche 11,25 … 55,25) und ist mit der Korrektur um 7 px
 *  gealtert. Es gilt:
 *  · **MIT Kennung** beginnt die Chip-Zeile bei 44,5 + h(Kennung) ⇒
 *    **~65,25**, die Flaeche endet also **~15–17 px VOR** ihr. Die Spanne ist
 *    die Rechenunsicherheit der Kennungshoehe (18,75 bei `lineHeight` 1,5,
 *    20,75 bei 1,66). Die Untergrenze ist **h − 3,75 px** (h = Hoehe der
 *    Kennungszeile) — sie folgt NICHT aus dem `mt: SP.chip` allein: mit h = 0
 *    reichte die Flaeche 3,75 px IN die Chip-Zeile. Positiv ist sie fuer jede
 *    Kennungszeile ueber 3,75 px (bei 12,5 px Schrift: 17,0 px).
 *  · **OHNE Kennung** — native Bundles tragen keine SKU
 *    (`backend/routes/products_api.py:577-588`) — beginnt die Chip-Zeile bei
 *    40,5, die Flaeche reicht **48,25 − 40,5 = 7,75 ≈ 8 px IN sie hinein**.
 *    Benannt und nicht behoben. Die `chips` sind gemessen keine Bedienelemente
 *    (`StatusPill` → `Pill` ohne `onClick` rendert ein `<span>`; die Kategorie-
 *    und Bundle-`Chip`s ebenso), aber der Satz „ein Tipp auf einen Chip darf
 *    nicht das Produkt oeffnen" gilt dort nur noch fuer die unteren zwei
 *    Drittel der Zeile. Die Alternative (`minHeight: 44` am `Link`) kostet
 *    JEDER Karte ~16 px Hoehe; die Seite fuehrt „eine Karte traegt keine
 *    Kennung" ohnehin schon als Defekt (`tests/listen/produkte.spec.ts`, P15).
 *
 *  ⚠️ **Beide Zahlen sind GERECHNET, nicht gemessen** (jsdom hat kein Layout;
 *  die Sonde dazu ist `tests/listen/produkte.spec.ts` F1–F5, und die fuehrt der
 *  Orchestrator). Die belastbare Untergrenze traegt die Kennungshoehe:
 *  **h − 3,75 px**.
 *
 *  🔴 **`export`, damit die Probe die Boden-Regel LESEN kann** (Hausform
 *  `KPI_LABEL_SX`, `KpiEtikett.test.jsx` E.1). jsdom wirft den
 *  `@media (pointer: coarse)`-Mantel um ein `::after` weg (gemessen: die Regel
 *  steht danach ohne Medienbedingung im Blatt) — aus dem gerenderten CSS ist
 *  die ZEIGER-Bedingung damit nicht lesbar, aus der Konstante schon. Die
 *  Render-Haelfte der Probe prueft daneben, dass die Regel den Titel wirklich
 *  erreicht, und dass der TRAEGER des Bodens kein `overflow: hidden` traegt. */
export const KARTE_TITEL_SX = {
  fontWeight: 700,
  // 🔴 `display: 'block'` haelt den Kasten auf BLOCK-Ebene — vorher war er es
  // durch `-webkit-box` (die Klemme lag am `Link`). Die Block-Ebene macht die
  // GEOMETRIE eindeutig: der `::after`-Boden haengt an einer Kante des
  // `Link`-Padding-Kastens, und die soll die des Kastens sein.
  // ⚠️ **Nicht weil ein Inline seinen Containing Block „verloere"** (so stand es
  // hier bis zur Nachbesserung 6, R16 W3, und das ist zu stark): nach CSS 2.1
  // §10.1 BLEIBT ein positioniertes Inline der Containing Block seiner absolut
  // positionierten Nachfahren. 🔴 **Was er dann ist — der Normtext, nicht die
  // Kurzfassung (R16 W3, Runde 4):** der Containing Block ist die
  // **Bounding-Box um die Padding-Boxen der ersten und der letzten Inline-Box**
  // dieses Elements. Bei einem umbrochenen Titel spannt diese Box also ueber
  // MEHRERE Zeilen — `top` haengt damit an einem Rechteck, das mehr umfasst als
  // die Textzeile, an der der Boden sitzen soll. (Die frueher hier stehende
  // Formulierung „die Vereinigung seiner Zeilenbox-Fragmente" meinte dasselbe
  // gemeinte Problem, war aber nicht der Wortlaut der Norm.) Block-Ebene gibt
  // genau EINEN Kasten, und nur darueber ist die Verankerung unten eine Aussage.
  display: 'block',
  position: 'relative',
  [GROBER_ZEIGER]: {
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      // 🔴 OBERKANTE verankert, nicht Unterkante: die Nachbarflaeche liegt hier
      // OBEN (die Vorkarte). Begruendung und Rechnung an `KARTE_TITEL_SX`.
      //
      // 🔴 **Der Wert kommt aus dem Theme, und das ist eine KORREKTUR
      // (R15 W1, Runde 4).** Hier stand `top: -SP.tight` mit der Begruendung,
      // MUI multipliziere nackte Zahlen bei `top`/`right`/`bottom`/`left` mit dem
      // Spacing-Faktor. **Diese Annahme ist falsch** — gemessen in
      // `@mui/system` 7.3.5, `styleFunctionSx/defaultSxConfig.js:247-250`:
      // `top: {}`, `right: {}`, `bottom: {}`, `left: {}` tragen KEIN
      // `themeKey: 'spacing'` (nur `m*`/`p*`/`gap` tun das). Eine nackte Zahl
      // wird zu px: `-SP.tight` = `-1` renderte **`top: -1px`**, nicht −8 px.
      // Die Flaeche lag damit 7 px zu TIEF — und der Fuehler F2 in
      // `tests/listen/produkte.spec.ts` (Konstante `UEBERSTAND = 8`) haette
      // daneben gemessen. Gegenprobe im Baum: `CompetitorModal.jsx:48`
      // (`top: 8`) steht sichtbar 8 px von der Ecke, nicht 64.
      //
      // Deshalb die Theme-Form: `t.spacing(SP.tight)` liefert `'8px'`, das
      // Minuszeichen steht davor ⇒ gerendert `top:-8px`. Kein px-Literal
      // (`check_ui_tokens.py`), und der Wert haengt am Faktor des Themes statt
      // an einer Annahme darueber. Die Probe
      // `ProductsTable.g1b-trefferflaeche.test.jsx` liest den GERENDERTEN Wert
      // aus dem Blatt (`top:-8px`), nicht die Quelle.
      top: (t) => `-${t.spacing(SP.tight)}`,
      height: TOUCH_TARGET_COARSE[GROBER_ZEIGER].minHeight,
    },
  },
};

/** Der Haus-Boden des **Karten**-Marken-Links (G1c-2; R16 B1 der Runde 1,
 *  2026-10-15; **auf die Karte eingeengt in der Nachbesserung 4**).
 *
 *  🔴 **Der Befund (R16 B1).** Die Spalte „Brand" brachte zwei neue Text-Links
 *  mit — die Marke in der Mobilkarte (`meta`, Aufrufstelle unten) und die
 *  Rueckfallebene in Band 2 der Produktzelle. Beide standen als
 *  `Link underline="hover" color="inherit"` ohne jede Trefferflaeche da; ein
 *  `caption`-Link ist ~17 px hoch und liegt damit unter der Norm-Untergrenze
 *  von **24 px** (WCAG 2.2 AA, 2.5.8) — die Haus-Regel verlangt **44**
 *  (N11.19, Regel 22 §8).
 *
 *  🔴 **Dieser Boden gehoert der KARTE, und nur ihr.** Die zwei Aufrufstellen
 *  tragen seit der Nachbesserung 5 **zwei verschiedene** Boeden, und der
 *  Unterschied ist Haus-Regel gegen Norm:
 *
 *  | Kontext | Boden | Token |
 *  |---|---|---|
 *  | Mobilkarte, `< sm`, grober Zeiger | **44** (Haus-Regel) | diese Konstante |
 *  | Tabellenzelle, ab `sm`, **jeder** Zeiger | **24** (Norm) | `DICHTER_TEXT_LINK_SX` |
 *
 *  🔴 **Hier stand bis zur Nachbesserung 5, `TOUCH_TARGET_COARSE_OFF` nehme den
 *  Boden in der Zelle „zurueck" — das war falsch (R15 W-1 = R16 B1 der Runde 3,
 *  2026-10-16).** Der Token steht ausschliesslich an den vier Knopf-Wurzeln
 *  (`theme.js` `MuiButton` `:697`, `MuiToggleButton` `:860`, klickbarer `Chip`
 *  `:973`, `MuiIconButton` `:1113`); `grep -n "MuiLink" frontend/src/theme/theme.js`
 *  liefert **0 Treffer**. Der Marken-Link ist ein `@mui/material/Link` und hatte
 *  dort **nie** einen Boden — an ihm wurde nichts zurueckgenommen, sondern nichts
 *  vergeben. Der Schluss (im dichten Kontext keinen 44-px-Boden) bleibt richtig;
 *  falsch war, eine **Analogie zu den Knoepfen** als **angewandte Regel**
 *  darzustellen.
 *
 *  🔴 **Und die Auslassung ist seit der Nachbesserung 5 keine mehr.** Der
 *  Band-2-Link traegt den **Norm-Boden 24** (`DICHTER_TEXT_LINK_SX`, WCAG 2.2 AA
 *  2.5.8, auf JEDEM Zeiger) — oben verankert, damit er in der eigenen
 *  ~50–56-px-Zeile endet statt in der Folgezeile. Die 44 bleiben der Karte: in
 *  der Zeile passen sie nicht.
 *
 *  🔴 **Und sie nehmen dem Nachbarn NICHT das Hit-Testing — korrigiert am
 *  2026-10-16 (R15 W-1, gemessen).** Hier stand „ohne dem Namens-Link des
 *  Nachbarn das Hit-Testing zu nehmen (R15 B-1)". Die Gegenprobe ist gefahren
 *  (Diagnose D44, ein 44er-Boden am Zell-Link): der Punkt `+25` unter der
 *  Link-Oberkante gehoert dem Link **trotzdem nicht**, und der Namens-Link der
 *  Folgezeile gewinnt seinen Streifen wie mit dem 24er-Boden. Den Ueberstand
 *  deckt die **Zeilenkante** — die Zeile endet 25 px unter der Link-Oberkante
 *  (gemessen), ab ihr malt die Zelle der Folgezeile darueber. Die Band-Klemme ist
 *  es **nicht**: ihre Padding-Box liegt bei ≈44 px (abgeleitet, nicht gemessen —
 *  Zeilenbox ≈19,9 px + 24 px Boden; vor der Nachbesserung 7 lag der Link noch
 *  als Inline-Box darin, seither ist er blockifiziert, s. `tokens.js`). Die Haus-Regel ist damit im
 *  Tabellenband **nicht** gehalten und die Norm schon — der Tausch ist benannt
 *  (R16 W2), nicht uebersehen.
 *
 *  🔴 **Was die erste Fassung stattdessen tat, und warum es falsch war.** Sie
 *  gab dem Band-2-Link einen **44**-px-Boden und versuchte, seinen Ueberstand in
 *  die Folgezeile mit einer Clip-Technik zu entschaerfen (`MARKE_ZEILE_BODEN`:
 *  `padding-bottom: 44px` + `margin-bottom: -44px` am `noWrap`-Traeger). Die
 *  Technik war netto null und ist als **Form** wieder da (`MARKE_BAND_KLEMME_SX`,
 *  unten) — falsch war die **Hoehe**: 44 px ragen in einer ~50–56-px-Zeile in die
 *  Folgezeile, und dort verliert der Namens-Link des Nachbarn das Hit-Testing.
 *
 *  🔴 **Der zweite Halbsatz ist am 2026-10-16 WIDERLEGT worden (R15 W-1,
 *  gemessen).** Mit einem 44er-Boden am Zell-Link (Diagnose D44, M2g bei 1024,
 *  grober Zeiger) ist der Punkt `+25` unter der Link-Oberkante **nicht** dem Link
 *  zugeordnet, und der Namens-Link der Folgezeile behaelt seinen Streifen
 *  (`markeImStreifen: false`) — genau wie mit dem 24er-Boden. Die Zeile endet
 *  **25 px** unter der Link-Oberkante (gemessen), und ab der Zeilenkante malt die
 *  Zelle der Folgezeile darueber; ein 44er-Boden kommt darueber nicht hinaus.
 *  „44 nimmt dem Nachbarn das Hit-Testing" ist damit keine Messung, sondern eine
 *  Annahme. Was bleibt: 44 passen **nicht in die Zeile** (25 px Platz), und sie
 *  ergaeben **keine groessere wirksame Flaeche** — die Norm 24 ist die richtige
 *  Zahl, die Haus-Regel 44 waere in dieser Zeile wirkungslos.
 *
 *  Die Begruendung, die damals dort stand — „nach unten gewinnt die spaetere
 *  Zeile das Hit-Testing (`pattern-library.md:51`)" — **trug nicht**: Bedingung 6
 *  jenes Abschnitts handelt von ZWEI vergroesserten Flaechen und setzt voraus,
 *  dass der spaetere Nachbar ebenfalls positioniert ist. Der Namens-Link der
 *  Folgezeile ist es nicht (`TITEL_KLEMME_SX` traegt kein `position`,
 *  `<tr>`/`<td>` ebenso wenig). ⚠️ **Was ihn deckt, ist die Zeilenkante, nicht
 *  die Positionierung** (gemessen 2026-10-16): die Annahme war in der Richtung
 *  richtig (der Nachbar gewinnt) und im Mechanismus falsch — und die Messung
 *  sagt, dass sie auch mit einem 44er-Boden gilt.
 *
 *  🔴 **An der OBERKANTE verankert (`top: 0`), die 44 px gehen nach UNTEN.**
 *  Die Nachbarflaeche, von der die Flaeche wegwachsen muss, liegt hier OBEN:
 *  in der Karte die Variantenzeile. Ein nach oben wachsender Boden ueberliefe
 *  sie.
 *
 *  🔴 **Als FLAECHE, nicht als Kasten** — dieselbe Bauart wie `KARTE_TITEL_SX`:
 *  ein `minHeight` am `Link` haette die Zeile (und mit ihr die Karte) gehoben.
 *  Der `::after` waechst nach aussen, ohne am Layout zu ruecken.
 *
 *  🔴 **`export`, damit die Probe die Boden-Regel LESEN kann** (Hausform
 *  `KARTE_TITEL_SX`, `KpiEtikett.test.jsx` E.1): jsdom wirft den
 *  `@media (pointer: coarse)`-Mantel um ein `::after` weg — aus dem gerenderten
 *  CSS ist die Zeiger-Bedingung nicht mehr lesbar, aus der Konstante schon. */
export const MARKE_KARTE_LINK_SX = {
  position: 'relative',
  [GROBER_ZEIGER]: {
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      height: TOUCH_TARGET_COARSE[GROBER_ZEIGER].minHeight,
    },
  },
};

/** Der Klemm-Traeger des **Band-2-Textes** — die Clip-Erweiterung, die den
 *  Norm-Boden des Links am Leben haelt, und seit der Nachbesserung 7 der
 *  **Flex-Rahmen**, der Vorderteil und Marke nebeneinander haelt.
 *
 *  ⚠️ **Die Ellipse sitzt seit der Nachbesserung 7 NICHT mehr hier**, sondern
 *  am Vorderteil (`MARKE_BAND_VORDERTEIL_SX`): das Band kuerzte den GANZEN
 *  Text und damit die Marke am Ende. Was hier bleibt, ist `overflow: hidden`
 *  als Schnittgrenze fuer den `::after`-Boden — die Erweiterung unten.
 *
 *  ⚠️ **Er ERSETZT das `noWrap` der Zeile vollstaendig** (die drei Eigenschaften
 *  stehen hier fuer alle Faelle). Ein `noWrap` daneben waere ein zweiter Wert
 *  fuer dieselbe Eigenschaft, und welcher gewinnt, haengt dann an der
 *  Einfuegereihenfolge der Klassen statt an dieser Zeile (Hausnotiz
 *  `KPI_LABEL_SX`).
 *  🔴 **Warum die Erweiterung noetig ist.** `DICHTER_TEXT_LINK_SX` haengt den
 *  Boden als `::after` **unter** die Textzeile (24 px ab der Oberkante des
 *  Links; die Zeilenbox ist ~17–20 px hoch). Ein `overflow: hidden` schneidet
 *  einen absolut positionierten Nachfahren auf seine **Padding-Box** ab
 *  (CSS 2.1 §11.1.1) — der Boden waere dann nur so hoch wie die Textzeile, und
 *  die Norm (24 px) waere nicht gehalten. `padding-bottom` in Bodenhoehe schiebt
 *  die Schnittgrenze nach unten, das gleich grosse negative `margin-bottom`
 *  stellt die Zeilenhoehe wieder her: **netto null**, die Zeile waechst nicht.
 *
 *  🔴 **Das ist KEIN zweiter Boden und keine Hoehenangabe der Zeile** — es
 *  verschiebt nur, WO geschnitten wird. Der Boden selbst bleibt 24 px.
 *
 *  🔴 **Und es ist der Grund, aus dem der Boden hier ueberhaupt wirkt:** ohne
 *  diese zwei Zeilen waere `DICHTER_TEXT_LINK_SX` am Band-2-Link unsichtbar und
 *  untastbar — genau der Zustand, den die Browserprobe `M2` (b1) rot faerbt. */
export const MARKE_BAND_KLEMME_SX = {
  // 🔴 **Nachbesserung 7 (2026-10-16): `flex` statt `block` — und die
  // Kuerzung wandert vom BAND auf den VORDERTEIL.**
  //
  // Der Befund (Orchestrator, Browser, A/B `g1c2f`, M2 (b5)): die Marke steht
  // am ENDE des Bandes, und das Band kuerzte den GANZEN Text. Bei
  // „078 Locating plate · Outer Burr · Locating plate" (1440, feiner Zeiger)
  // waren von der 47,86 px breiten Marke nur **17 px** sichtbar und tippbar —
  // der Rest lag hinter der Bandkante. Der `::after`-Boden (24 px, Norm
  // 2.5.8) endete an derselben Kante.
  //
  // 🔴 **Die Regel, die jetzt gilt: die Marke ist das BEDIENELEMENT des Bandes
  // und wird NIE ZUGUNSTEN DES VORDERTEILS gekuerzt.** Der Vorderteil („SKU · Variante") kuerzt sich mit
  // Ellipse; die Marke gibt keinen Pixel ab (`flexShrink: 0`, s.
  // `MARKE_BAND_MARKE_SX`).
  //
  // ⚠️ **„nie gekuerzt" stand hier bis zur Nachbesserung 8 (2026-10-16) ohne
  // Einschraenkung — und war zu absolut (R16 B1 der Runde 5).** `flexShrink: 0`
  // verhindert nur das SCHRUMPFEN. Ist die Marke breiter als der Platz, den das
  // Band ihr laesst, laeuft sie ueber dessen `overflow: hidden` hinaus und wird
  // an der Bandkante abgeschnitten — genau der Gegenfall, den dasselbe Haus
  // anderswo selbst nennt (`shp_vendors.metaobject_name` bis 49 Zeichen).
  // Seit der Nachbesserung 8 traegt die Marke dafuer die **Hausform der
  // gekuerzten Texte** als Rueckfall (`MARKE_BAND_KUERZUNG_SX`): sie kuerzt
  // sich dann SELBST, sichtbar als Ellipse, und der volle Name steht im
  // `title`. Die Regel lautet damit: **kein Pixel zugunsten des Vorderteils —
  // und allein zu breit kuerzt sie sich selbst.**
  //
  // `display: flex` ist die Form, die das traegt: nur im Flex-Kontext kann ein
  // Glied unter seine Inhaltsbreite schrumpfen (`minWidth: 0`) und ein anderes
  // es nicht (`flexShrink: 0`). Mit `display: block` gaebe es dafuer kein
  // Mittel — ein `inline-block` mit `maxWidth` kann die Breite des Nachbarn
  // nicht kennen.
  //
  // ⚠️ **Der Preis, benannt (Regel 17 §1):** ein Flex-Glied ist BLOCKIFIZIERT,
  // seine Box ist damit die Zeilenbox (~20 px) statt der Inline-Box (~14 px).
  // Der Boden haengt trotzdem an der Oberkante (`top: 0`), und die Klemme ist
  // netto null (s. unten) — die Zeilenhoehe aendert sich nicht.
  display: 'flex',
  overflow: 'hidden',
  // Bleibt am Band: sie wird von ALLEN drei Gliedern geerbt (Vorderteil,
  // Trenner, Marke) und haelt jedes einzeilig.
  //
  // 🔴 **Nachbesserung 8 (2026-10-16, R16 W3): hier stand „der Marken-TEXT
  // (ohne `vendor_id`, ein nackter Textknoten) erbt sie" — den nackten
  // Textknoten gibt es seit der Nachbesserung 7 nicht mehr.** Der Text-Zweig
  // ist ein `<Box component="span">`, der Link-Zweig war nie einer; ein
  // `grep` findet im Band keinen Textknoten mehr. Die Eigenschaft bleibt
  // richtig (sie haelt die Zeile einzeilig), ihre Begruendung zeigte auf einen
  // Zustand, den es nicht mehr gibt.
  whiteSpace: 'nowrap',
  paddingBottom: `${DICHTER_TEXT_LINK_SX['&::after'].height}px`,
  marginBottom: `-${DICHTER_TEXT_LINK_SX['&::after'].height}px`,
  // 🔴 **M2g-Nachbesserung (2026-11-04): der Padding-Kasten ist KLICKDURCHLASSEND.**
  //
  // Der Padding-Kasten (unten) ist 24 px hoch, damit `overflow: hidden` den
  // Boden nicht abschneidet — die **Zeile** hat darunter aber nur ~15 px Platz
  // (gemessen: Band 752–792, Zeilenkante 777). Der Kasten ragt deshalb in die
  // Folgezeile, und `elementFromPoint` sieht ihn **auch ohne Hintergrund**:
  // genau das misst M2g (`produkte.spec.ts:1092-1098`) und fiel auf
  // „078S HOPPER LID" rot — der Namensstreifen der Folgezeile gehörte dem Band.
  //
  // 🔴 **Die Geometrie bleibt, wie sie ist** — und das ist eine Entscheidung,
  // keine Bequemlichkeit: die 24 px sind an anderer Stelle GEPINNT
  // (`__tests__/ProductsTable.brand-rueckfall.test.jsx` prüft
  // `padding-bottom:24px` / `margin-bottom:-24px` als Zusage „das Band klippt
  // den Boden nicht weg"). Ein kleinerer Wert wäre dort ein Rotbeweis gegen eine
  // Absicht, die weiter gilt.
  //
  // Was hier falsch war, ist allein die **Trefferfläche**: Padding ist keine
  // Fläche. `pointer-events: none` am Band nimmt seinen eigenen Kasten aus dem
  // Hit-Test, `& > *` gibt sie den Gliedern zurück — der Marken-LINK und sein
  // `::after`-Boden bleiben tippbar (M2 b1/b5), Vorderteil und Trenner ebenso.
  // *Widerlegt hätte es:* eine rote M2-Zeile (der Boden wäre dann weg) oder
  // eine weiterhin rote `nameStreifen`-Zeile (dann läge der Deckel woanders).
  pointerEvents: 'none',
  '& > *': { pointerEvents: 'auto' },
};

/** Der **Vorderteil** des Bandes 2 — „SKU · Variante". Das Glied, das sich
 *  KUERZT (Nachbesserung 7, 2026-10-16).
 *
 *  🔴 **Der KERN ist `overflow: hidden`, nicht `minWidth: 0`** — korrigiert in
 *  der Nachbesserung 8 (2026-10-16, R15 W-1, gemessen). Hier stand „`minWidth:
 *  0` ist der Kern". Die automatische Mindestgroesse eines Flex-Gliedes ist
 *  `min-width: auto` = die MIN-CONTENT-Breite, **aber nur bei
 *  `overflow: visible`** (CSS Flexbox §4.5): `overflow: hidden` setzt sie
 *  ohnehin auf 0. Das ist am Mutanten gemessen — G9 („Vorderteil ohne
 *  `minWidth: 0`") ist ein **aequivalenter Mutant** und bleibt gruen, weil
 *  `overflow: hidden` die Mindestbreite schon aufhebt.
 *
 *  🔴 **Der Rotweg dieser Achse ist deshalb ein anderer, und er ist gemessen:
 *  `flexShrink: 0` am VORDERteil (G11).** Dann kann das Glied gar nicht mehr
 *  schrumpfen, die Zeile laeuft ueber die Bandkante, und die Marke endet
 *  ausserhalb des Bandes — die Browserprobe `M2` (b6) wird rot. `minWidth: 0`
 *  bleibt trotzdem stehen: es ist die ausdrueckliche, vom
 *  `overflow`-Schluesselwort unabhaengige Fassung derselben Zusage (Hausform
 *  „einen Wert nicht zweimal, aber die Zusage zweimal belegen").
 *
 *  🔴 **`overflow: hidden` hier schneidet NICHTS vom Boden ab:** der Boden
 *  (`::after`) haengt am Marken-LINK, nicht an diesem Glied — der Vorderteil
 *  ist kein Link und traegt keinen. */
const MARKE_BAND_VORDERTEIL_SX = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Der **Trenner** zwischen Vorderteil und Marke („ · ").
 *
 *  🔴 **Ein eigenes Glied, kein Textknoten im Band.** Im Flex-Kontext wird ein
 *  nackter Textknoten zu einem ANONYMEN Flex-Glied, und in dessen eigenem
 *  Block fallen fuehrende und nachfolgende Leerzeichen als Zeilenrand weg —
 *  aus „SKU… · Marke" wuerde „SKU…·Marke". `whiteSpace: 'pre'` haelt beide
 *  Leerzeichen.
 *
 *  🔴 **Er bleibt in voller Breite (`flexShrink: 0`):** er ist Trenner, nicht
 *  Inhalt — und er ist die Grenze, an der man sieht, dass gekuerzt wurde.
 *  🔴 **Er steht VOR der Marke, nicht hinter dem Vorderteil:** so bleibt er
 *  stehen, wenn der Vorderteil sich kuerzt („SKU · Varia… · acaia" statt
 *  „SKU · Varia…acaia"). */
const MARKE_BAND_TRENNER_SX = {
  whiteSpace: 'pre',
  flexShrink: 0,
};

/** Die **Marke** im Band — als Link ODER als blosser Text (ohne `vendor_id`):
 *  das Glied, das dem VORDERteil keinen Pixel abgibt (Nachbesserung 7; auf den
 *  Rueckfall eingeschraenkt in der Nachbesserung 8, 2026-10-16).
 *
 *  🔴 **`flexShrink: 0` an BEIDEN Aufrufstellen** — sie sind dieselbe Zeile
 *  Text, und zwei Bauformen fuer denselben Text waeren die naechste Abweichung.
 *  Die **volle Breite zugunsten des Vorderteils** ist keine Kosmetik: der Link
 *  traegt den Norm-Boden (WCAG 2.2 AA 2.5.8, `DICHTER_TEXT_LINK_SX`), und ein
 *  vom BAND durchschnittener Link haette
 *  eine Trefferflaeche, die an der Bandkante endet — genau der Befund.
 *
 *  ⚠️ **Nur die Marke ist ein eigenes Glied, der Link selbst bleibt direktes
 *  Kind des Bandes.** Die Browserprobe liest das Band als `parentElement` des
 *  Marken-Links (`frontend/tests/listen/produkte.spec.ts`, M2 (b3)/(b4)) — ein
 *  Mantel um den Link verschoebe diesen Zugriff auf ein Element ohne
 *  Clip-Erweiterung und faerbte (b3) rot. */
const MARKE_BAND_MARKE_SX = {
  flexShrink: 0,
  // 🔴 **Nachbesserung 8 (2026-10-16, R16 B1 + W2 der Runde 5): der RUECKFALL
  // der Marke, wenn sie ALLEIN breiter ist als ihr Platz.**
  //
  // `flexShrink: 0` allein sichert nur gegen das SCHRUMPFEN. Ist die Marke
  // breiter als der Platz, den das Band ihr laesst, laeuft sie ueber dessen
  // `overflow: hidden` hinaus und wird an der Bandkante abgeschnitten — genau
  // der Fall, den die Runde 5 als „die Marke wird NIE gekuerzt" zu absolut
  // beschrieben fand. `maxWidth` deckelt sie jetzt, und die Kuerzungs-Hausform
  // (`MARKE_BAND_KUERZUNG_SX`) macht daraus eine SICHTBARE Ellipse statt eines
  // harten Schnitts; der volle Name steht im `title` (Aufrufstellen unten).
  //
  // 🔴 **Warum `calc(100% - 1em)` und nicht `100%` — abgeleitet, nicht
  // gemessen (Regel 17 §1).** Vor der Marke steht der **Trenner** (` · `,
  // `MARKE_BAND_TRENNER_SX`) als eigenes Glied mit `flexShrink: 0`. Mit einem
  // glatten `maxWidth: '100%'` endete der Marken-Kasten um die Trennerbreite
  // HINTER der Bandkante (das Band kuerzt den Vorderteil bis auf 0, der
  // Trenner bleibt): die Ellipse wuerde jenseits der Kante gezeichnet und vom
  // `overflow: hidden` des Bandes mit abgeschnitten — die Marke waere hart
  // abgeschnitten, die Ellipse nie zu sehen. Der Trenner ist bei `caption`
  // (12 px) rund **0,83 em** breit (zwei Leerzeichen + `·` in Roboto); `1em`
  // reserviert seinen Platz **mit Reserve**.
  // ⚠️ **Der Rest, benannt:** bei einer Ersatzschrift mit breiteren Leerzeichen
  // koennte die Reserve aufgebraucht werden und die Ellipse um wenige Pixel
  // beschnitten werden. Der `title` traegt den vollen Namen in jedem Fall.
  maxWidth: 'calc(100% - 1em)',
};

/** Die **Kuerzungs-Hausform der Marke** — dieselben vier Zeilen wie
 *  `MARKE_KLEMME_SX` und `MARKE_BAND_VORDERTEIL_SX`: `display: block` plus die
 *  drei Eigenschaften, die aus einem zu langen Text eine sichtbare Ellipse
 *  machen (Nachbesserung 8, 2026-10-16, R16 B1).
 *
 *  🔴 **Sie sitzt NICHT am Link, obwohl dort der `maxWidth`-Deckel steht.** Der
 *  Link traegt den Norm-Boden (`::after`, `DICHTER_TEXT_LINK_SX`), und
 *  `overflow: hidden` auf DEMSELBEN Element schnitte ihn auf dessen
 *  Padding-Box ab (CSS 2.1 §11.1.1) — genau der Mechanismus, den
 *  `MARKE_BAND_KLEMME_SX` fuer das BAND beschreibt. Der Link behaelt deshalb
 *  seine Kastenform, und der Text bekommt einen inneren Traeger. **Der Link
 *  bleibt direktes Kind des Bandes** (die Browserprobe liest das Band als
 *  `parentElement` des Marken-Links) — der Mantel sitzt INNEN.
 *
 *  ⚠️ **Am TEXT-Zweig (ohne `vendor_id`) liegt sie direkt am Glied:** dort gibt
 *  es keinen Boden, der abgeschnitten werden koennte. */
const MARKE_BAND_KUERZUNG_SX = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Der `sx` des **Marken-LINKS** im Band — der Norm-Boden, an der RECHTEN
 *  Kante verankert (Nachbesserung 8, 2026-10-16; R15 W-2 = R16 W1 der Runde 5).
 *
 *  🔴 **Der Befund.** `DICHTER_TEXT_LINK_SX` spannt den Boden mit `left: 0;
 *  right: 0` ueber den Link-Kasten und laesst ihn mit `minWidth: 24` nach
 *  **rechts** wachsen. Am Band ist das die falsche Richtung: die Marke steht am
 *  ENDE der Zeile, und wenn der Vorderteil hat schrumpfen muessen, liegt ihre
 *  rechte Kante **auf** der Bandkante — die 4 px Zuwachs fallen dann in das
 *  `overflow: hidden` des Bandes und werden mitgeklippt; die wirksame Breite
 *  faellt auf die Textbreite der Marke zurueck, bei einer 3-Zeichen-Marke
 *  (prod: `ROK`, 58 Produkte mit ≤ 3 Zeichen) also **unter 24**. Dieselbe Kante
 *  nennt der Kommentar am Boden-Aufruf selbst.
 *
 *  🔴 **Die Reparatur ist der ANKER, nicht die Zahl.** `left: 'auto'; right: 0`
 *  verankert den Boden an der rechten Kante des Links — `minWidth: 24` waechst
 *  dann nach LINKS, in den Innenraum des Bandes hinein, wo nichts sie klippt.
 *
 *  🔴 **Nachbesserung 9 (2026-10-16) — der Anker ALLEIN trug nicht; erst mit
 *  `width: '100%'` ist der Boden der des Links.** Hier stand bis dahin
 *  „`width: auto` loest dann auf die Inhaltsbreite des `::after` auf (0) und
 *  `minWidth: 24` macht daraus **genau 24 px, die nach LINKS wachsen**" — das
 *  beschreibt die Auflösung richtig und uebersieht ihre Folge: ein absolut
 *  positioniertes Element mit `left: auto`, `right: 0` und `width: auto` ist
 *  **shrink-to-fit** (CSS 2.1 §10.3.7). Der Kasten ist damit nicht „der Link,
 *  mindestens 24 px breit", sondern **genau 24 px an der RECHTEN Kante** — jeder
 *  Pixel des Links links davon liegt ausserhalb der Trefferflaeche. Im Browser
 *  gemessen (A/B `g1c2h`, unmutiert): `M2g` faellt auf dem Zweig rot, sobald die
 *  Marke breiter als ~48 px ist — der Punkt `m.left + m.width / 2`, 23 px unter
 *  der Link-Oberkante, gehoert dem Link nicht mehr (die drei laengsten Marken der
 *  Sicht). `width: '100%'` spannt den Boden ueber die ganze Linkbreite; die
 *  Prozentbreite eines absolut positionierten Kastens gilt gegen die Padding-Box
 *  seines positionierten Vorfahren, und das ist der Link selbst. `minWidth: 24`
 *  greift weiterhin, wenn der Link schmaler ist, und waechst dann nach links —
 *  das Ziel der Nachbesserung 8 bleibt unberuehrt.
 *
 *  ⚠️ **Die zwei Werte werden GESPROCHEN, nicht geerbt:** ein blosses
 *  `{ ...MARKE_BAND_MARKE_SX }` neben `{ ...DICHTER_TEXT_LINK_SX }` wuerde den
 *  Schluessel `&::after` **ersetzen** und damit `content`, `position`, `top`,
 *  `height` und `minWidth` verlieren — der Boden waere weg. Deshalb der
 *  ausdrueckliche Spread.
 *
 *  ⚠️ **Die Spalten-Fassung bleibt `left: 0; right: 0`** (`DICHTER_TEXT_LINK_SX`
 *  unveraendert): der Block-Link der Brand-Spalte ist so breit wie die Zelle,
 *  `minWidth` greift dort nicht, und ein nach links wachsender Boden haette
 *  keinen Grund. */
const MARKE_BAND_LINK_SX = {
  ...DICHTER_TEXT_LINK_SX,
  ...MARKE_BAND_MARKE_SX,
  '&::after': { ...DICHTER_TEXT_LINK_SX['&::after'], left: 'auto', right: 0, width: '100%' },
};

/** Der **Vordertext** des Bandes 2 als Zeichenkette — EINMAL gebildet, weil er
 *  zweimal gebraucht wird: als sichtbarer, kuerzbarer Text UND vollstaendig im
 *  `title` (Hausform der gekuerzten Texte: `title={r.product_name}` am
 *  Namens-Link, `TITEL_KLEMME_SX`).
 *
 *  🔴 Der volle Text steht ohnehin im DOM (die Kuerzung ist nur visuell) — der
 *  `title` ist fuer den Menschen mit der Maus, nicht fuer den Screenreader.
 *  Zwei Stellen mit derselben Zeichenkette waeren die naechste Abweichung,
 *  deshalb eine Funktion statt zweier Ausdruecke. */
const markeBandVordertext = (r, isBundle) =>
  `${isBundle ? 'View components' : r.sku}`
  + (r.variant_name && r.variant_name !== r.product_name ? ` · ${r.variant_name}` : '');

/** Der Klemm-Traeger des Marken-Links — in der Mobilkarte UND in der
 *  Tabellenzelle: die Zeile kuerzt wie bisher (eine Zeile, Auslassung), auf
 *  einem Element, das **nicht** der Boden-Traeger ist. Dieselbe Bauart wie
 *  `TITEL_KLEMME_SX` am Kartentitel.
 *
 *  🔴 **In beiden Faellen eine Bedingung, seit der Nachbesserung 5.** Beide
 *  Links tragen einen `::after`-Boden (die Karte 44, die Zelle 24) —
 *  `overflow: hidden` auf DEMSELBEN Element schnitte ihn auf die Padding-Box ab
 *  (CSS 2.1 §11.1.1), daher der innere Traeger. Er ist an beiden Aufrufstellen
 *  derselbe: die zwei Bedingungen sind dieselbe Zeile Text, und zwei Bauformen
 *  fuer denselben Text waeren die naechste Abweichung.
 *
 *  ⚠️ In der Zelle traegt nicht der Link diesen Traeger, sondern das ganze
 *  Band — dort ist die Klemme `MARKE_BAND_KLEMME_SX` (mit der Clip-Erweiterung,
 *  die den 24-px-Boden durchlaesst). */
const MARKE_KLEMME_SX = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

// §3.6 — der Zeilenklick landet auf dem PRODUKT, nicht auf einem Markt.
// Vorher waehlte `buildRecommendationLink` den Markt mit dem groessten
// absoluten `diff_total` und schickte den Bediener dorthin: das Ziel der
// Hauptnavigation hing damit an der Zeile, und ein Produkt MIT Empfehlung
// oeffnete eine andere Seite als eines ohne. Die Empfehlung steht jetzt IN der
// Spalte `Price reco` (§4.4), der Markt ist dort lesbar — der Zeilenklick
// braucht ihn nicht mehr. Die Markt-Tiefenverknuepfung
// `/products/:sku/markets/:marketId` bleibt als Route bestehen und wird von der
// Marktauswahl auf der Detailseite weiter benutzt.
const productRowTarget = (sku) => (sku ? `/products/${encodeURIComponent(sku)}` : '/products');

// §4.4 — die Empfehlung als GROESSE + ANZAHL statt als Bit.
// `fetchRecSummary` rechnet `markets[]` mit `diff_total` je Markt aus und warf
// Betrag und Anzahl vor dem Rendern weg; uebrig blieb ein gruener Haken, der
// einen Ausgleich von 0,05 EUR nicht von einer Luecke von 40 EUR unterschied.
const REC_ALIGNED_EUR = 0.5;
const fmtGapEur = (v) =>
  `${new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(Number(v) || 0))} €`;

const recPill = (markets, failed = false) => {
  // 🔴 `failed` heisst: der Abruf ist gescheitert, die Zahl ist UNBEKANNT —
  //    nicht null (Regel 22 Nr. 3). Das ist ein anderer Zustand als „keine
  //    Empfehlung" und darf nicht dieselbe Ruecklage tragen.
  //
  // 🔴 **Der Strich ist ein EN-Dash, kein EM-Dash (G1a, §2.5).** §8.1 legt die
  //    Hausbedeutung fest: `–` (en) heisst „wir wissen es nicht", `—` (em) ist
  //    die Ruecklage fuer „es gibt nichts". Der Zustand hier ist der erste —
  //    der Abruf ist gescheitert, die Antwort ist UNBEKANNT. Die Ruecklage
  //    darunter („keine Empfehlung") traegt weiter den EM-Dash.
  if (failed) return { tone: 'plain', label: '– unknown' };
  const ms = Array.isArray(markets) ? markets : [];
  if (ms.length === 0) return { tone: 'plain', label: '— no data' };
  const byAbs = ms
    .map((m) => ({
      code: String(m?.market_code || '').toUpperCase(),
      abs: Math.abs(Number(m?.diff_total ?? 0)),
    }))
    .sort((a, b) => b.abs - a.abs);
  const top = byAbs[0];
  if (!top || top.abs < REC_ALIGNED_EUR) return { tone: 'ok', label: '✓ Aligned' };
  const where = byAbs.length === 1
    ? marketLabel(top.code)
    : `${byAbs.length} markets`;
  return {
    tone: 'warn',
    label: where ? `▲ ${fmtGapEur(top.abs)} · ${where}` : `▲ ${fmtGapEur(top.abs)}`,
  };
};

// ---------- bundle components panel body (page-scoped, not a common/ component) ----------
// Fetches GET /api/products/bundle/<product_id>/components when a bundle row is
// clicked and lists the component variants through the SHARED
// `components/products/BundleComponentList` — the same rows the bundle detail
// page shows (one component, so the two surfaces cannot drift again). The fetch
// and the cost footer stay here; the panel is only ever rendered by
// ProductsTable's SlidePanel. Named export for its test.
//
// 🔴 The overview folds a bundle to ONE row, so this panel lists the MERGED
// components of all variants. When the variants carry different components the
// backend withholds the parent total (`parent.rolled_up_cost = null`) — that is
// "varies by variant", NOT "no component carries a cost" (the old footer said
// the latter next to visible line costs). The panel says the list is merged
// and points to the bundle page, which shows one variant.
export function BundleComponentsContent({ productId, bundleSku }) {
  const [state, setState] = useState({
    loading: true, error: null, components: [], variants: [], rolledUpCost: null,
    partial: false, costSource: null,
  });
  const [reloadSeq, setReloadSeq] = useState(0);

  useEffect(() => {
    if (!productId) return undefined;
    let alive = true;
    setState({
      loading: true, error: null, components: [], variants: [], rolledUpCost: null,
      partial: false, costSource: null,
    });
    axios
      .get(noCache(`${API_BASE}/api/products/bundle/${encodeURIComponent(productId)}/components`))
      .then((res) => {
        if (!alive) return;
        const parent = res.data?.parent || {};
        setState({
          loading: false,
          error: null,
          components: res.data?.components || [],
          variants: res.data?.variants || [],
          rolledUpCost: parent.rolled_up_cost ?? null,
          partial: !!parent.partial,
          costSource: parent.cost_source ?? null,
        });
      })
      .catch(() => {
        if (alive) {
          setState({
            loading: false, error: 'Could not load components.', components: [], variants: [],
            rolledUpCost: null, partial: false, costSource: null,
          });
        }
      });
    return () => { alive = false; };
  }, [productId, reloadSeq]);

  const variantsDiffer = bundleVariantsDiffer(state.variants);
  const loaded = !state.loading && !state.error;
  // Cost roll-up DISPLAY. Seit A1.2 (2026-10-09) rechnet der Endpunkt mit
  // DEMSELBEN Kostenbegriff wie die Preisautomatik (variantengebunden,
  // `backend/bundle_kosten.py`), und `cost_source` sagt, WOHER der Wert kommt:
  // Summe der Bestandteile (primaer) oder eigene Kosten der Bundle-Variante
  // (Rueckfall). Display-only; never used for FCA / cost accounting.
  const cost = describeBundleCost({
    rolledUpCost: state.rolledUpCost,
    partial: state.partial,
    components: state.components,
    variantsDiffer,
    costSource: state.costSource,
  });

  return (
    <>
      {bundleSku && (
        <Box sx={{ px: SP.card, py: SP.sm, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Button
            component={RouterLink}
            to={`/products/${encodeURIComponent(bundleSku)}`}
            variant="outlined"
            size="small"
          >
            Open bundle page
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: SP.chip }}>
            One variant: its components, stock and cost
          </Typography>
        </Box>
      )}

      {loaded && variantsDiffer && state.components.length > 0 && (
        <Alert severity="info" sx={{ mx: SP.card, mt: SP.sm }}>
          Components differ between variants — this list merges all of them.
          {bundleSku ? ' The bundle page shows one variant.' : ''}
        </Alert>
      )}

      <Box sx={{ px: SP.card, py: SP.sm }}>
        <BundleComponentList
          components={state.components}
          loading={state.loading}
          error={state.error}
          onRetry={() => setReloadSeq((n) => n + 1)}
        />
      </Box>

      {/* 🔴 `rolledUpCost === null` heisst UNBEKANNT, nicht null. Bis zum 2026-08-18
          lieferte das Backend dafuer `0.0`, und hier stand "Σ 0,00 €" in Fettschrift
          — eine erfundene Zahl mit voller Autoritaet. Beide Gates: Blocker. Die Zeile
          bleibt stehen und sagt aus, was sie weiss — Wortwahl aus `describeBundleCost`. */}
      {loaded && state.components.length > 0 && (
        <Box sx={{
          px: SP.card, py: SP.sm, borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Rolled-up cost
            </Typography>
            <Typography
              variant="subtitle2"
              component="p"
              color={cost.known ? 'text.primary' : 'text.secondary'}
              sx={{ fontWeight: cost.known ? 700 : undefined }}
            >
              {cost.value}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: SP.chip }}>
            {cost.note}
          </Typography>
          {/* 🔴 Die QUELLE, nicht nur der Grund (R15 W-NEU-1, 2026-10-09): `note`
              nennt den Grund, diese Zeile die Herkunft aus `cost_source`. */}
          <BundleCostProvenance costSource={cost.source} />
        </Box>
      )}
    </>
  );
}

// ---------- component ----------
export default function ProductsTable() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Breiten (G1b, Plan §2.6 / Konzept `inhaltsbreite.md` §3) ──────────────
  // 🔴 Die Spaltenentscheidung liest die **Behälter**-Breite, nicht den
  // Viewport (§3.1, NW3.1:548): die Seitenleiste wandert um ±40 px, während der
  // Viewport steht — zwei verschiedene Signale. Der `ref` sitzt am
  // `liste-tabelle`-Kasten (dem RAUM), nicht am `TableViewport` (dem
  // gedeckelten Ergebnis), sonst mässe sich der Deckel selbst
  // (`ListenMuster.jsx:798-803`, dieselbe Bauart).
  const tabelleRef = useRef(null);
  const { width: behaelterBreite } = useContainerWidth(tabelleRef);
  // 🔴 „Nicht gemessen" trägt die **Musterbreite**, nie 0 (§4.3:623-624). Für
  // `full` trägt `CONTENT_WIDTH.full` `null` — der zweite `??` fällt dort
  // DURCH, statt das Muster zu überstimmen (R16-W-N2).
  const messBasis = behaelterBreite ?? CONTENT_WIDTH.full ?? CONTENT_WIDTH.reading;
  const { visible: sichtbareSpalten } = useColumnPriority({
    columns: SPALTEN,
    containerWidth: messBasis,
  });
  // Die sichtbaren `key`s als Menge — die Kopfzeile und JEDE Zelle fragen sie.
  const sichtbar = useMemo(() => new Set(sichtbareSpalten.map((s) => s.key)), [sichtbareSpalten]);
  // Das SPALTENBUDGET = Σ `COLUMN_MAX[kind].max` der sichtbaren Spalten
  // (§4.1:567). `?? 0`: eine in `production` als `kern` behandelte Spalte mit
  // unbekannter Art hat kein `max` — EIN falscher Deskriptor darf nicht die
  // ganze Tabelle reissen (`useColumnPriority` meldet ihn bereits).
  const spaltenBudget = sichtbareSpalten.reduce(
    (summe, sp) => summe + (COLUMN_MAX[sp.kind]?.max ?? 0),
    0,
  );
  // Die TABELLENBREITE = min(Behälter, Budget) (§4.1:568). `TableViewport`
  // **meldet** diesen Wert nur (BW6); das **Anwenden** ist Sache des
  // Verbrauchers — hier als `maxWidth` am Rollbehälter (PW4/PW4b).
  const tabellenMax = Math.min(messBasis, spaltenBudget);
  // 🔴 Die UNTERGRENZE der Tabelle = Σ `COLUMN_MAX[kind].min` der sichtbaren Spalten
  // (N9.6/NW3.5: erst UNTER dieser Breite rollt die Tabelle und greift die klebende
  // Kennung + der Kantenhinweis). 🔴 Bis zur G1b-Nachbesserung stand hier ein FESTES
  // `minWidth: 880` (Befund 1) — ein Rest aus der Zeit VOR der Prioritaets-Auswahl: bei
  // 1280 (Behaelter 904, Budget 680) erzwang es eine 880-px-Tabelle in einem auf 680
  // gedeckelten Kasten, `List` war angeschnitten und der Kasten rollte in sich. Die Zahl
  // folgt jetzt den SICHTBAREN Spalten, nicht einer Konstante.
  const tabellenMin = sichtbareSpalten.reduce(
    (summe, sp) => summe + (COLUMN_MAX[sp.kind]?.min ?? 0),
    0,
  );
  // Die Breiten der sichtbaren Spalten als ANTEIL des Budgets (§3.3): mit
  // `tableLayout: 'fixed'` nimmt der Browser sie aus der ersten Zeile (dem
  // Kopf), und die Spalten springen nicht mehr (N9.5, P33).
  const spaltenBreiten = spaltenBudget > 0
    ? sichtbareSpalten.map((sp) => ({
        width: `${(((COLUMN_MAX[sp.kind]?.max) ?? 0) / spaltenBudget) * 100}%`,
      }))
    : null;

  // data
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  // 🔴 Der Grund eines leeren Tisches. Vorher gab es keinen: `fetchOverview`
  // hatte `try { … } finally { }` OHNE `catch`, eine abgelehnte Antwort liess
  // `rows` auf `[]` stehen und der Bediener sah einen leeren Tisch, der von
  // „kein Filter passt" nicht zu unterscheiden war. Das ist der schlechteste
  // Zustand der Liste (Konzept §7.3).
  const [overviewError, setOverviewError] = useState(null);
  // 🔴 **„Es sind schon einmal Zeilen angekommen"** (Nachbesserung G1a,
  // 2026-10-14, R16 B3). Der Sperr-Riegel des ersten Aufbaus (N8.5) hing bis
  // hierher an `Boolean(overviewError)` — also an JEDEM gescheiterten Abruf. Das
  // widerspricht N4.5: ein Fehler NACH einer Änderung lässt „die Bedienelemente
  // ihren gewählten Wert behalten und **bedienbar** bleiben". Ein vorübergehender
  // 500 nach einer Sucheingabe deaktivierte damit genau das Feld, in das der
  // Bediener gerade tippt. Der Riegel greift nur, solange die Liste noch NIE
  // aufgebaut war — danach stehen die alten Zeilen weiter im Baum (`setRows`
  // läuft im `catch` nicht) und die Bedienelemente bleiben bedienbar.
  const [jeGeladen, setJeGeladen] = useState(false);
  // 🔴 Der Empfehlungs-Abruf trug frueher einen stillen Fang (`.catch(() => …)`).
  //    Fiel er aus, trugen ALLE Zeilen „— no data", und der KPI-Filter
  //    „Needs price action" meldete „kein Treffer" — beides eine Behauptung
  //    ueber den Katalog, wo die Wahrheit „ich konnte nicht nachsehen" ist
  //    (Regel 22 Nr. 3). Der Grund wird jetzt gefuehrt.
  const [recSummaryError, setRecSummaryError] = useState(null);
  const [recSummaryBySku, setRecSummaryBySku] = useState({});
  // 🔴 Der Statusfilter VOR dem Pill „In stock, hidden" (B1, Form a). Der Pill
  // SETZT den Statusfilter — sonst behauptete der Empfangschip `active, draft`,
  // während versteckte Zeilen in der Tabelle stehen. Beim Ausschalten wird
  // DIESER Zustand zurückgegeben, nicht hart `ACTIVE_STATUSES`: eine vorher von
  // Hand gesetzte Auswahl darf ein bloßes Durchklicken nicht wegwerfen.
  const statusVorHidden = useRef(null);

  const [categoryInfoOpen, setCategoryInfoOpen] = useState(false);
  const openCategoryInfo = (e) => { e?.stopPropagation?.(); setCategoryInfoOpen(true); };
  const closeCategoryInfo = () => setCategoryInfoOpen(false);

  // Bundle components SlidePanel — opened by clicking a bundle row. Holds the
  // parent product_id + name; the panel body (BundleComponentsContent) fetches
  // the components endpoint by product_id. The row's SKU (bundles carry a
  // `90…` SKU since Plan A, task 3; older rows may still be NULL) feeds the
  // panel's "Open bundle page" link — shown only when there is a SKU.
  const [bundlePanel, setBundlePanel] = useState(null); // { productId, productName, sku } | null
  const openBundlePanel = (r) => setBundlePanel({
    productId: r.product_id, productName: r.product_name || '(bundle)', sku: r.sku || null,
  });
  const closeBundlePanel = () => setBundlePanel(null);

  // 🔴 Der EINE Filter, der NICHT in die URL geht (§3.5): eine SKU-Menge aus
  // dem Portfolio-Brett kann bis zu tausenden Einträge tragen und ist damit
  // URL-untauglich (N10.1). Benannte Ausnahme; der Träger des Chips ist die
  // Z3-Zeile (§3.5), die Ansicht reicht der Leiste ein erweitertes `query`.
  const [skuFilterSet, setSkuFilterSet] = useState(null); // Set<string> or null
  const notifyDashboardClear = () =>
  window.dispatchEvent(new CustomEvent('portfolio-dashboard:clear-filter'));


  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setOverviewError(null);
    try {
      const prodRes = await axios.get(noCache(OVERVIEW_URL_TABLE));
      setRows(Array.isArray(prodRes.data) ? prodRes.data : []);
      // Erst ein ERFOLGREICHER Aufbau zählt (R16 B3) — auch ein leerer Katalog
      // (`[]`) ist einer: die Liste ist aufgebaut, nur ohne Zeilen.
      setJeGeladen(true);
    } catch (err) {
      // Kein stiller Fang: der Grund wird gezeigt (Konzept §7.3). `pattern-
      // library.md:633-635` verbietet `.catch(() => {})` auf einem Abruf, der
      // eine sichtbare Flaeche traegt — und der leere Tisch WAR die stille
      // Variante.
      setOverviewError(
        err?.response?.data?.error || err?.message || 'Products could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecSummary = useCallback(async () => {
    setRecSummaryError(null);
    let recRes;
    try {
      recRes = await axios.get(noCache(RECS_URL));
    } catch (err) {
      // Kein stiller Fang (pattern-library.md:633-635): der Abruf traegt eine
      // sichtbare Spalte und einen KPI-Filter. Der Grund wird gezeigt.
      setRecSummaryError(
        err?.response?.data?.error || err?.message || 'Price recommendations could not be loaded.'
      );
      setRecSummaryBySku({});
      return;
    }
    // build per-SKU map of markets that actually have a recommendation
    const bySku = {};
    for (const it of (recRes.data.items || [])) {
      if (!it?.has_recommendation) continue;

      const seen = new Set();
      for (const m of (it.markets || [])) {
        const code = String(m.market_code || '').toUpperCase();
        const rec  = Number(m.recommended_total ?? m.recommended_price ?? NaN);
        const diff = Number(m.diff_total ?? NaN);

        // only keep if we actually have a meaningful change
        const meaningful =
          Number.isFinite(diff) ? Math.abs(diff) >= 0.005 : Number.isFinite(rec);

        if (!code || seen.has(code) || !meaningful) continue;
        seen.add(code);

        (bySku[it.sku] ||= { markets: [] }).markets.push({
          market_id: m.market_id,
          market_code: code,
          recommended_total: rec,
          diff_total: Number.isFinite(diff) ? diff : null,
        });
      }
    }
    setRecSummaryBySku(bySku);
  }, []);

  // 🔴 `refreshAll` ruft nur noch ZWEI Abrufe (G1a, §1.2). Der dritte
  // (`/api/products/returns_rate`) ist entfallen: die Spalte „Return rate" wurde
  // mit §4.2 geschnitten, kein `head`-Eintrag und keine Zelle las die zwei Werte
  // `returns_rate`/`returns_90d` — der Abruf trug tote Daten. Die Route selbst
  // bleibt (Backlog-Karte „Rückbau prüfen", §9 #21a).
  const refreshAll = useCallback(async () => {
    await fetchOverview();
    fetchRecSummary();
  }, [fetchOverview, fetchRecSummary]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // keep in sync with detail actions
  useEffect(() => {
    const refresh = () => refreshAll();
    window.addEventListener('price-updated', refresh);
    window.addEventListener('pricing-strategy-changed', refresh);
    window.addEventListener('recommendation-suppressed', refresh);
    window.addEventListener('recommendation-accepted', refresh);
    return () => {
      window.removeEventListener('price-updated', refresh);
      window.removeEventListener('pricing-strategy-changed', refresh);
      window.removeEventListener('recommendation-suppressed', refresh);
      window.removeEventListener('recommendation-accepted', refresh);
    };
  }, [refreshAll]);

  // Die zwei Zuhörer des Portfolio-Bretts (§1.2 W10). 🔴 **EIN Zuhörer je
  // Ereignis, nicht zwei:** bis G1a stand `apply-filter` ZWEIMAL in dieser
  // Datei (einmal mit einem toten `setActiveFilterChip`, einmal ohne). Beide
  // setzten dieselbe Menge; der zweite war eine zweite Wahrheit über denselben
  // Zustand. Die Wirkung ist dieselbe, die Aufnahme führt jetzt eine Zeile.
  useEffect(() => {
    const onApply = (e) => {
      const skus = e?.detail?.skus || [];
      if (!Array.isArray(skus) || !skus.length) return;
      setSkuFilterSet(new Set(skus));
    };
    const onClear = () => setSkuFilterSet(null);
    window.addEventListener('portfolio-dashboard:apply-filter', onApply);
    window.addEventListener('portfolio-dashboard:clear-filter', onClear);
    return () => {
      window.removeEventListener('portfolio-dashboard:apply-filter', onApply);
      window.removeEventListener('portfolio-dashboard:clear-filter', onClear);
    };
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(rows.map((r) => r.category_abcd).filter(Boolean))).sort(),
    [rows]
  );
  const productTypes = useMemo(
    () => Array.from(new Set(
      rows.map((r) => r.taxonomy_canonical_type || r.shopify_product_type).filter(Boolean)
    )).sort(),
    [rows]
  );

  // 🔴 **G1c-2 (§2.2/§0.1): die Werte der zwei neuen Facetten kommen aus den
  // ANGEKOMMENEN Zeilen — wie `categories`/`productTypes`, NICHT aus einem
  // zweiten Abruf.** `/api/product_overview` liefert seit G1c-1 je Zeile
  // `vertical_key`/`vertical_label` und `vendor_id`/`vendor_label`; damit gibt es
  // weder einen zweiten Fehlerweg noch einen zweiten Abruf (`J-A1`).
  // 🔴 Der URL-Wert ist der STABILE Schlüssel (`vertical_key` bzw.
  // `shp_vendors.id`, §2.4), das Label nur die Anzeige (§0.1: „stabile
  // Backend-Werte, nie Etiketten").
  // 🔴 Sortiert wird case-insensitiv (`sensitivity: 'base'`) — dieselbe Regel
  // wie an der Brand-Spalte (OE-G1c-1), damit „timemore" nicht hinter „Varia"
  // landet.
  const verticals = useMemo(() => {
    const m = new Map();
    let ohne = false;
    rows.forEach((r) => {
      if (r.vertical_key) {
        if (!m.has(r.vertical_key)) m.set(r.vertical_key, r.vertical_label || r.vertical_key);
      } else {
        ohne = true;
      }
    });
    const liste = Array.from(m, ([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    // 🔴 **D-3a: „not set" ist ein FACETTENWERT, kein Vertical.** Ohne ihn
    // waeren die Zeilen ohne `product_vertical_assignment` nur durch Blaettern
    // erreichbar — und genau die sind die Arbeitsliste des Backfills
    // (Plan §6.4). Er steht am ENDE und nicht in der alphabetischen Reihe: er
    // ist keine Vertikale, und eine Einordnung unter „n" behauptete das.
    // 🔴 Nur wenn es solche Zeilen GIBT: eine Option, die nie trifft, waere
    // eine Behauptung ueber den Bestand, die die gelieferten Zeilen widerlegen.
    // 🔴 Die Facette ist die EINZIGE Quelle der Werte (kein zweiter Abruf,
    // `J-A1`); die Folge ist benannt: eine inaktive Vertikale MIT Produkten
    // erscheint, eine aktive OHNE nicht (Plan §6.4, Nachtrag 2026-11-04).
    if (ohne) liste.push({ value: VERTICAL_NONE, label: VERTICAL_NONE_LABEL });
    return liste;
  }, [rows]);
  const vendors = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      // `vendor_id === null` heisst: kein Lieferant verknuepft — dann gibt es
      // auch keinen Facettenwert (§0.1 Nr. 3, 2 Zeilen auf prod).
      if (r.vendor_id && !m.has(r.vendor_id)) {
        m.set(r.vendor_id, r.vendor_label || r.vendor_id);
      }
    });
    return Array.from(m, ([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [rows]);

  // ── Der Zustand der Abfrage (BS1, §3) ───────────────────────────────────
  // 🔴 **Ein Schreiber je Seite (§3.3): `useListQuery`.** Die sieben Parameter
  // der Karte (§3.1) — `q`, `status`, `category`, `type`, `quick`, `sort`,
  // `page` — liegen in der Adresse, nicht mehr in sieben `useState`. Der Grund
  // ist der Bestand: ein geteilter Link trug bisher NICHTS vom Filterzustand,
  // und ein Neuladen warf ihn weg.
  //
  // 🔴 Die Facetten-`values` sind die ANGEKOMMENEN Werte, nicht die erhofften:
  // `categories`/`productTypes` kommen aus `rows` (dem, was der Endpunkt
  // geliefert hat). Eine Facette ohne Werteliste prüft nichts und meldet nichts
  // (`useListQuery` §`@param`).
  const dimensionen = useMemo(() => [
    // Freitext (keine `values`): nur so entsteht der Chip „Search: <Begriff>"
    // in Z3 — einen zweiten Kanal gibt es nicht.
    { key: 'q', kind: 'single', label: 'Search' },
    // Die Vorgabe ist eine ECHTE Einschränkung (nicht die Nullmenge) — sie
    // trägt deshalb den Vorgabe-Chip „Status: active, draft · default" in Z3,
    // und sein ✕ ist kein No-op (§2.2).
    { key: 'status', kind: 'multi', label: 'Status', values: STATUS_OPTIONS, default: ACTIVE_STATUSES },
    { key: 'category', kind: 'single', label: 'Category', values: categories },
    {
      key: 'type',
      kind: 'single',
      label: 'Product type',
      values: productTypes.map((p) => ({ value: p, label: getCanonicalTypeDisplayName(p) })),
    },
    { key: 'quick', kind: 'quick', label: 'Quick filter', values: QUICK_OPTIONS },
    // 🔴 **G1c-2 (§2.2): die zwei neuen Dimensionen sind FACETTEN IM BLATT, nicht
    // in Z1.** Die Leiste laesst genau DREI Z1-Facetten zu und wirft bei mehr
    // (`ListToolbar.jsx:229-233`, Konzept §3.1:266 Punkt 5b) — `vertical` und
    // `vendor` sind die vierte und fünfte und stehen deshalb in
    // `moreFilters.criteria` (unten), dem Weg, den der Baustein schon hat.
    // 🔴 Beide MEHRFACH (Baum §5: „Status, Lieferant, Markt: Mehrfach") — das
    // rendert `FilterSelect` (`Select multiple`), nicht die Glas-Pille.
    // 🔴 Der Wert ist der stabile Schluessel (§2.4), die Beschriftung des Chips
    // das Anzeige-Label (`etikett` in `useListQuery`).
    { key: 'vertical', kind: 'multi', label: 'Vertical', values: verticals },
    { key: 'vendor', kind: 'multi', label: 'Vendor', values: vendors },
  ], [categories, productTypes, verticals, vendors]);

  const query = useListQuery({
    dimensions: dimensionen,
    sort: { default: 'product_name', values: SORT_WERTE },
    pageSize: ROWS_PER_PAGE,
  });
  const { state, set } = query;

  const search = state.q ?? '';
  // 🔴 `LEER_ARRAY` ist eine MODULKONSTANTE, kein `[]`-Literal je Render: ein
  // frisches `[]` liesse jeden `useMemo`, der die Liste in den Abhängigkeiten
  // führt, bei JEDEM Render neu rechnen (und ESLint meldet es). Die zwei
  // mehrwertigen Dimensionen liefern ohnehin immer ein Array — die Konstante
  // ist die Rücklage für den Fall, dass die Dimension fehlt.
  const statusFilter = state.status ?? LEER_ARRAY;
  const categoryFilter = state.category ?? '';
  const productTypeFilter = state.type ?? '';
  const quickListe = state.quick ?? LEER_ARRAY;
  // G1c-2 (§2.2): die zwei neuen Dimensionen sind MEHRWERTIG — die Ruecklage ist
  // dieselbe Modulkonstante wie bei `status` (ein frisches `[]` je Render liesse
  // jeden `useMemo` mit dieser Liste neu rechnen).
  const verticalFilter = state.vertical ?? LEER_ARRAY;
  const vendorFilter = state.vendor ?? LEER_ARRAY;

  // Die Sortierung: `sort` trägt `feld` (aufsteigend) oder `-feld`.
  const sortFeld = state.sort ?? 'product_name';
  const order = sortFeld.startsWith('-') ? 'desc' : 'asc';
  const orderBy = sortFeld.startsWith('-') ? sortFeld.slice(1) : sortFeld;
  // Die Seite des Bausteins ist 1-basiert, `pagedRows` rechnet 0-basiert.
  const page = Math.max(0, (Number(state.page) || 1) - 1);
  // Precise A–D classification logic (from classify_products_mcda.py)
  // Concise header tooltip
  const CATEGORY_TOOLTIP_SHORT = 'A–D classes from 6-month revenue & margin performance.';

  // Modal data (used to render nicely formatted content)
 const CATEGORY_DETAILS = {
   window: 'Last 6 months',
   weights: { revenue: 0.70, margin: 0.30 },
   metrics: [
     { label: 'Revenue (6m)', value: 'Σ(quantity × paid_amount)' },
     { label: 'Margin (6m)',  value: 'Σ(quantity × margin_absolute)' },
   ],
   shares: [
     { label: 'rev_share',    value: 'revenue6 / total_rev' },
     { label: 'margin_share', value: 'margin6 / total_margin' },
   ],
   combined: 'combined = 0.70 × rev_share + 0.30 × margin_share',
   rank: 'Sort SKUs by combined (descending) and compute cumulative combined (cum_combined).',
   buckets: [
     { k: 'A', range: 'cum_combined ≤ 0.70',  note: 'Top contribution to combined share' },
     { k: 'B', range: '0.70 < cum_combined ≤ 0.90', note: 'Strong contributors' },
     { k: 'C', range: '0.90 < cum_combined ≤ 0.99', note: 'Long tail' },
     { k: 'D', range: 'cum_combined > 0.99', note: 'Very small contribution' },
   ],
   persistence: 'Result is written to shp_products.category_abcd and shown here as “Category”.'
 };

const processedRows = useMemo(() =>
  rows.map((r) => {
    const net = Number(r.list_price ?? 0) > 0 ? Number(r.list_price) / 1.19 : 0;
    const cost = Number(r.cost_per_item ?? 0);
    return {
      ...r,
      sku: String(r.sku || ''),
      product_name: r.product_name || '',
      variant_name: r.variant_name || '',
      product_status: String(r.product_status || '').trim().toLowerCase(),
      product_type: r.taxonomy_canonical_type || r.shopify_product_type || '',
      // 🔴 D-3a: der EINE abgeleitete Vertical-Text. **Gelesen wird er nur noch
      // von `VerticalMark`** (Namenszelle und mobile Karte) — Zelle und
      // Sortierung sind mit der `xl`-Spalte am 2026-11-04 entfallen. Der
      // FILTER liest `r.vertical_key` (unten, `dimensionen`), nicht diesen Text:
      // er filtert den stabilen Schlüssel, angezeigt wird das Label.
      // Reihenfolge: Anzeigename aus `product_verticals` (`vertical_label`),
      // sonst der stabile Schluessel (eine Vertikale ohne Label ist nicht
      // „leer"), sonst „not set" (Regel 22 §3; R16 C2: dasselbe Wort wie im
      // Produktdetail).
      vertical: r.vertical_label || r.vertical_key || VERTICAL_NONE_LABEL,
      cost_per_item: cost,
      current_inventory: Number(r.current_inventory ?? 0),
      incoming_inventory: Number((r.incoming_inventory ?? r.incoming_stock) ?? 0),
      list_price: Number(r.list_price ?? 0),
      net_list_price: net,
      margin_pct: margeProzentVomNetto(net, cost),
      rrp: Number(r.rrp ?? 0),
      average_sales_30d: Number(r.average_sales_30d ?? 0),
      // Backend (/api/product_overview) returns the stockout date as
      // `estimated_stockout_date`; the table cell, the `stockouts` quick-filter,
      // and the mobile soon-badge all read `stock_out_date`. Normalize here so
      // the column populates + becomes sortable. (reorder_date already matches.)
      stock_out_date: r.estimated_stockout_date || null,
      // 🔴 Die Empfehlung als ZAHL, nicht als Bit (Konzept §4.4). `fetchRecSummary`
      // rechnet `markets[]` mit `diff_total` je Markt schon aus und warf bisher
      // Betrag UND Anzahl weg — der gruene Haken trug genau ein Bit, und ein
      // Ausgleich von 0,05 € sah aus wie eine Luecke von 40 €. Hier wird die
      // Groesse greifbar: `rec_abs_diff` ist der groesste Absolutwert, damit die
      // Spalte danach sortierbar ist.
      rec_markets: recSummaryBySku[String(r.sku || '')]?.markets || [],
      rec_abs_diff: (() => {
        const ms = recSummaryBySku[String(r.sku || '')]?.markets || [];
        return ms.reduce(
          (m, x) => Math.max(m, Math.abs(Number(x?.diff_total ?? 0))), 0
        );
      })(),
    };
  }), [rows, recSummaryBySku]
);

  // 1) Filtered
  const filteredRows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = processedRows
      .filter((r) => statusFilter.length === 0 || statusFilter.includes(r.product_status))
      .filter((r) => !categoryFilter || r.category_abcd === categoryFilter)
      .filter((r) => !productTypeFilter || r.product_type === productTypeFilter)
      // G1c-2 (§2.2): die zwei neuen Dimensionen — clientseitig ueber `rows`,
      // derselbe Weg wie `status`/`category`/`type`. Der Vergleich ist der rohe
      // Schluessel (`vertical_key` / `vendor_id`), also genau der URL-Wert (§2.4).
      // 🔴 **D-3a: eine Zeile OHNE Vertikale erfuellt jetzt den Leerwert
      // `VERTICAL_NONE`** — vorher erfuellte sie keinen Filter, und der
      // Leerzustand war ueber die Facette gar nicht erreichbar (Plan §6.4: die
      // Zeilen ohne Zuordnung sind die Arbeitsliste des Backfills). Fuer den
      // Vendor-Filter bleibt es bei §0.1 Nr. 3 (kein Facettenwert ohne Wert).
      // 🔴 **Der Vertical-Filter ist CLIENTSEITIG und bleibt es** (R15 B2): der
      // Endpunkt kennt keinen `?vertical=`-Parameter, genau wie `status`,
      // `category` und `type`. Zwei Implementierungen desselben Kriteriums
      // koennen driften; eine davon hatte keinen Aufrufer.
      .filter((r) => verticalFilter.length === 0
        || verticalFilter.includes(r.vertical_key || VERTICAL_NONE))
      .filter((r) => vendorFilter.length === 0 || vendorFilter.includes(r.vendor_id))
      .filter((r) => !q || [r.sku, r.product_name, r.variant_name].some((f) => (f || '').toLowerCase().includes(q)));

    if (skuFilterSet && skuFilterSet.size) {
      base = base.filter((r) => skuFilterSet.has(r.sku));
    }

    // Die Quick-Filter (§2.3) — 🔴 **kombinierbar UND verknüpft:** jede gedrückte
    // Pille engt weiter ein. Bis G1a war es EIN Wert (`quickFilter === 'x'`); die
    // Reihe führt jetzt mehrere, und zwei Pillen sind zwei Bedingungen, nicht
    // zwei Alternativen.
    if (quickListe.length > 0) {
      const heute = new Date();
      const inTagen = (d) => (d - heute) / (1000 * 60 * 60 * 24);
      const praedikate = {
        with_recs: (r) => (recSummaryBySku[r.sku]?.markets || []).length > 0,
        // Exclude bundle parents: a bundle has no intrinsic inventory of its own,
        // so it must not count toward / surface under the in-stock filter.
        in_stock: (r) => !r.is_bundle && Number(r.current_inventory) > 0,
        // „In stock, hidden" — auf Lager UND in Shopify nicht sichtbar.
        // 🔴 Der Statussatz steht HIER, obwohl der Pill den Statusfilter beim
        // Einschalten selbst setzt (B1, Form a). Die Doppelung ist Absicht: sie
        // hält die Aussage der Pille wahr, wenn der Bediener die Statusauswahl
        // danach von Hand ändert — ein Status ausserhalb `HIDDEN_STATUSES` kann
        // keine Zeile HINZUFÜGEN, nur welche wegnehmen. Der Pill benutzt damit
        // die normale Pipeline; `skuFilterSet` (KPI-Kreuzfilter), Suche, Category
        // und Product type laufen unverändert mit.
        in_stock_hidden: (r) =>
          !r.is_bundle
          && Number(r.current_inventory) > 0
          && HIDDEN_STATUSES.includes(r.product_status),
        low_margin: (r) => {
          const marge = margeProzentVomNetto(r.net_list_price, r.cost_per_item);
          return !r.is_bundle && marge != null && marge < 20;
        },
        stockouts: (r) => {
          if (r.is_bundle) return false;  // bundle parents never count as stocking out
          const raw = r.stock_out_date || r.stockout_date || r.predicted_stockout_date;
          const sd = raw ? new Date(raw) : null;
          return sd && isFinite(sd) && inTagen(sd) <= 14;
        },
      };
      for (const key of quickListe) {
        const trifft = praedikate[key];
        if (trifft) base = base.filter(trifft);
      }
    }
    return base;
  }, [processedRows, search, statusFilter, categoryFilter, productTypeFilter, quickListe, verticalFilter, vendorFilter, recSummaryBySku, skuFilterSet]);

  // 2) Sorted
  const sortedRows = React.useMemo(() => {
    const compare = (a, b) => {
      const A = a[orderBy];
      const B = b[orderBy];
      // string-like
      // 🔴 **G1c-2: `brand` gehoert in DIESEN Zweig** (§2.3, drittes Sortierziel).
      // Faellt es in den numerischen Rueckfall, vergleicht es `NaN` und sortiert
      // still gar nicht — der Klick saehe richtig aus und taete nichts.
      // 🔴 Und der Vergleich ist **case-insensitiv** (`sensitivity: 'base'`,
      // OE-G1c-1): die sechs Doppel-Schreibweisen (M-10: `timemore`/`Timemore` …)
      // stehen dann NEBENEINANDER, statt dass `Varia` zwischen sie faellt. Die
      // DATEN bleiben roh — nur die Reihenfolge ist gefaltet, nichts wird
      // umgeschrieben.
      // 🔴 Der Vertical-Sortierzweig ist am 2026-11-04 entfallen, zusammen mit
      // der Spalte: ohne Kopf gibt es keinen Klick, und ein Sortierwert ohne
      // Spalte waere eine zweite Wahrheit. `SORT_WERTE` bleibt (beide
      // Richtungen der uebrigen Spalten).
      if (['sku', 'product_name', 'brand', 'product_status', 'category_abcd', 'product_type'].includes(orderBy)) {
        const res = String(A || '').localeCompare(String(B || ''), undefined, { sensitivity: 'base' });
        return order === 'asc' ? res : -res;
      }
      // date columns — parse to time; null/invalid always sort LAST (both directions)
      if (['stock_out_date', 'reorder_date'].includes(orderBy)) {
        const toTime = (v) => {
          const t = v ? new Date(v).getTime() : NaN;
          return Number.isFinite(t) ? t : null;
        };
        const aT = toTime(A);
        const bT = toTime(B);
        if (aT === null && bT === null) return 0;
        if (aT === null) return 1;   // a (empty) after b
        if (bT === null) return -1;  // b (empty) after a
        return order === 'asc' ? aT - bT : bT - aT;
      }
      // numeric fallback
      const aN = Number(A ?? 0);
      const bN = Number(B ?? 0);
      return order === 'asc' ? aN - bN : bN - aN;
    };
    return filteredRows.slice().sort(compare);
  }, [filteredRows, order, orderBy]);

  // 3) Paged (use this when rendering)
  const pagedRows = React.useMemo(() => {
    const start = page * ROWS_PER_PAGE;
    return sortedRows.slice(start, start + ROWS_PER_PAGE);
  }, [sortedRows, page]);

  // 4) Die Seite klemmen (N10.4) — der Hook kennt die Trefferzahl nicht, die
  //    Ansicht kürzt `page` gegen die letzte Seite. Ohne das zeigte ein Link auf
  //    `?page=9` nach einem Filterwechsel eine LEERE Seite.
  React.useEffect(() => {
    const letzteSeite = Math.max(1, Math.ceil(sortedRows.length / ROWS_PER_PAGE));
    if (state.page > letzteSeite) set('page', letzteSeite);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `state.page` wird geklemmt, nicht als Auslöser gelesen
  }, [sortedRows.length]);

  // 🔴 Der SKU-Filter ist der EINE Filter ausserhalb der Adresse (§3.5) — er
  // geht deshalb auch nicht durch den Hook, und die Seite muss ihn hier von Hand
  // zurücksetzen (N10.3: jede Abfrageänderung setzt `page` auf 1). Der erste
  // Lauf wird übersprungen: beim Aufbau hat noch niemand geblättert.
  const ersteSkuSetzung = useRef(true);
  React.useEffect(() => {
    if (ersteSkuSetzung.current) { ersteSkuSetzung.current = false; return; }
    set('page', 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur der SKU-Filter bewegt die Seite hier
  }, [skuFilterSet]);

  // ── Z3 (§2.2) — die Chips kommen aus dem Hook, der SKU-Chip kommt dazu ───
  // 🔴 **Der Träger ist benannt (§3.5 W3):** `ListToolbar` reicht Z3
  // ausschliesslich `query.chips` und `query.clearAll` durch — die ANSICHT
  // reicht ein erweitertes `query` ein. Beides trägt den lokalen Chip ohne
  // Bausteinaenderung.
  const skuChip = skuFilterSet ? {
    id: 'sku',
    label: `SKU subset (${skuFilterSet.size})`,
    onRemove: () => { setSkuFilterSet(null); notifyDashboardClear(); },
  } : null;
  // 🔴 **`clearAll` wird UMGESCHLOSSEN, nicht überschrieben (W-N7):** die
  // Hook-Funktion leert die ADRESSE (jede Dimension auf ihre Vorgabe, `sort` und
  // `page` fallen). Der SKU-Filter fällt hier zusätzlich, und das Ereignis für
  // das Portfolio-Brett feuert UNBEDINGT — für seinen Zuhörer ist ein Aufruf
  // ohne gesetzten SKU-Filter ein No-op.
  const z3Query = {
    chips: skuChip ? [...query.chips, skuChip] : query.chips,
    clearAll: () => { query.clearAll(); setSkuFilterSet(null); notifyDashboardClear(); },
  };

  // 🔴 Die Zahl am „Filters"-Knopf (N4.6). Gezählt werden die BLATT-Kriterien
  // MIT WERT — **einschliesslich der Vorgabe:** der Status steht auf
  // `['active','draft']`, und das ist eine echte Einschränkung, also zählt er.
  // Ein Kriterium ohne Wert zählt nicht.
  const statusIstStandard =
    statusFilter.length === ACTIVE_STATUSES.length &&
    ACTIVE_STATUSES.every((s) => statusFilter.includes(s));
  const produktFilterAktiv =
    (statusFilter.length > 0 ? 1 : 0) + (categoryFilter ? 1 : 0) + (productTypeFilter ? 1 : 0);

  // Die Sortierung schreibt in die Adresse (N10.2): ein zweiter Klick auf
  // dieselbe Spalte dreht die Richtung, eine andere Spalte beginnt aufsteigend.
  const handleRequestSort = (property) => {
    if (orderBy === property) set('sort', order === 'asc' ? `-${property}` : property);
    else set('sort', property);
  };

  // 🔴 Der eine Quick-Filter, der den Statusfilter MITSETZT (B1, Form a). Alle
  // anderen Pillen lassen den Statusfilter unangetastet.
  const applyQuickFilter = (naechste) => {
    const liste = Array.isArray(naechste) ? naechste : [];
    const hatteHidden = quickListe.includes('in_stock_hidden');
    const hatHidden = liste.includes('in_stock_hidden');
    if (hatHidden && !hatteHidden) {
      // Nur beim EINSCHALTEN merken — ein zweiter Klick auf die gedrückte Pille
      // kommt mit derselben Liste zurück und darf den Merker nicht überschreiben.
      statusVorHidden.current = statusFilter;
      // Kopie, nicht die Modulkonstante (R15 W2): heute mutiert niemand den
      // State in place, aber eine spätere In-Place-Änderung würde sonst
      // `HIDDEN_STATUSES` für jeden Render korrumpieren.
      set('status', [...HIDDEN_STATUSES]);
    } else if (!hatHidden && hatteHidden) {
      // Verlassen: den Zustand VOR dem Pill zurückgeben.
      set('status', statusVorHidden.current ?? ACTIVE_STATUSES);
    }
    set('quick', liste);
  };

  // 🔴 8 Spalten, nicht 13 (Konzept §4.2). Geschnitten sind:
  //   Status        — der Standardfilter IST bereits `active+draft`; das
  //                   archivierte Faellchen ist ein Filter, keine Spalte.
  //   Product type  — ein Taxonomie-Internes in der Bedienerliste.
  //   RRP           — eine Detailseiten-Sache.
  //   Reorder       — zwei benachbarte Datumsspalten fuer EINE Entscheidung;
  //                   steht jetzt als Unterzeile an Stock-out.
  //   Return rate   — bei 90 d kein Triage-Signal.
  // Neu: `Price reco` (§4.4) ersetzt den gruenen Haken durch Betrag + Markt.
  const head = [
    { id: 'product_name', label: 'Product', numeric: false },
    // 🔴 **G1c-2: die Kopfzeile heisst „Brand" — die SPALTE fuehrt die MARKE**
    // (`shp_products.vendor`), waehrend der FILTER „Vendor" heisst und den
    // LIEFERANTEN meint (OE-G1c-1, §0.1: zwei Woerter, zwei Dinge). Die Zelle
    // verlinkt trotzdem auf den Lieferanten — aber nur, wenn `vendor_id` da ist.
    // Sortierbar wie jede sichtbare Kopfzelle (`TableSortLabel` unten) — der
    // `sort`-Wert `brand` steht in `SORT_FELDER`.
    { id: 'brand', label: 'Brand', numeric: false },
    {
      id: 'category_abcd',
      label: (
        <Tooltip
          title={
            <Box sx={{ maxWidth: 300 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {CATEGORY_TOOLTIP_SHORT}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                Weights: <b>70% revenue</b>, <b>30% margin</b>.
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption">
                <Link
                  component="button"
                  onClick={openCategoryInfo}
                  onMouseDown={(e) => e.stopPropagation()}
                  underline="always"
                  sx={{ color: '#fff', fontWeight: 500 }}
                >
                  Read more
                </Link>
              </Typography>
            </Box>
          }
          arrow
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <span>Category</span>
            <IconButton
              size="small"
              onClick={openCategoryInfo}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Category explanation"
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Box>
        </Tooltip>
      ),
      numeric: false
    },
    // 🔴 Die Vertical-SPALTE ist am 2026-11-04 entfallen (Regel 8: PW4b/PW5b/
    // PW7 — ab ~2400 px sprengte sie das Breitenbudget). Den Wert tragen die
    // Ausnahme-Marke in der Namenszelle (`VerticalMark`), der Filter und die
    // mobile Karte; „not set" bleibt der Text des Leerzustands (Regel 22 §3).
    // Incoming ist in On hand eingezogen (`33 (+6)`) — die mobile Karte macht
    // das seit immer so, und zwei Spalten fuer eine Zahl sind eine zu viel.
    { id: 'current_inventory', label: 'On hand', numeric: true },
    { id: 'stock_out_date', label: 'Stock-out', numeric: false },
    { id: 'list_price', label: 'List', numeric: true },
    // EK-A19: die Skala gehoert in den Namen — die Portfolio-Ansicht zeigt
    // daneben Klassen aus dem AUFSCHLAG auf den EK, nicht aus dieser Zahl.
    // 🔴 G1b-Nachbesserung 2 (2026-10-14, Fotobefund 3): `label` ist der
    // ZUGÄNGLICHE Name (EK-A19; `tests/products/margen-zwei-skalen.spec.ts`
    // A19.1 prüft ihn wörtlich), `kurz` die SICHTBARE Fassung. Der Kopf ist
    // `nowrap` (N9.1, `TableViewport`) und darf nicht überlaufen
    // (`inhaltsbreite.md` §0.2: „mit Prioritäten darf der Überlauf gar nicht
    // erst entstehen"). Gemessen am 2026-10-14: die Textbox ist bei 1440
    // ~111 px, die volle Fassung braucht ~128 px — der Kopf stand als
    // `MARGIN % (OF NI` da. `Margin % (net)` (~96 px) behält das
    // unterscheidende „net" sichtbar.
    { id: 'margin_pct', label: 'Margin % (of net)', kurz: 'Margin % (net)', numeric: true }, // calculated on render
    { id: 'average_sales_30d', label: '30d Sales', numeric: true },
    { id: 'rec_abs_diff', label: 'Price reco', numeric: true },
  ];

  // 🔴 G1b: gerendert werden nur die SICHTBAREN Spalten (BW5). Ohne diese Zeile
  // wäre die Spaltenzahl konstant und PW5 („die Spaltenzahl wächst mit der
  // Breite") nicht messbar — die Musterroute macht es genauso
  // (`ListenMuster.jsx:294-305`). Die Menge kommt aus `SPALTEN`, nicht aus einer
  // zweiten Liste: `head` trägt die BESCHRIFTUNG (JSX), `SPALTEN` die Stufe.
  const sichtbarerKopf = head.filter((h) => sichtbar.has(h.id));

  // §7.2/§2.5 — vier Leerzustände, nicht einer. Ein fehlgeschlagener Abruf, ein
  // wirklich leerer Katalog und eine Filterkombination ohne Treffer sind drei
  // verschiedene Lagen mit drei verschiedenen Auswegen — `EmptyState` verlangt
  // den Grund als Pflicht-Prop, genau damit sie nicht mehr zusammenfallen.
  //
  // 🔴 **Der Fehler beim ERSTEN Aufbau sperrt die Leiste** (N8.5). Erkannt am
  // gesetzten `overviewError`: `fetchOverview` leert ihn am Anfang jedes Laufs,
  // ein gescheiterter Aufbau trägt ihn also bis zum nächsten Versuch.
  //
  // 🔴 **Und NUR beim ersten Aufbau** (R16 B3, 2026-10-14): `&& !jeGeladen`.
  // N4.5 trennt die zwei Lagen ausdrücklich — nach einer Änderung bleiben die
  // Bedienelemente bedienbar, auch wenn der Abruf scheitert. Ohne diese zweite
  // Bedingung sperrte jeder Fehler, und der Bediener verlöre das Feld, in dem er
  // gerade tippt. `leerGrund` bleibt davon unberührt: der Fehler wird weiterhin
  // als Fehler GEMELDET (EmptyState + Grund-Alert), nur die Sperre fällt weg.
  const sperre = Boolean(overviewError) && !jeGeladen;

  const leerGrund = overviewError ? 'error'
    : (quickListe.includes('with_recs') && recSummaryError) ? 'error'
    : rows.length === 0 ? 'no-data'
    : sortedRows.length === 0 ? 'no-match'
    : null;

  // 🔴 §8.1 kennt ZWEI no-match-Wortlaute (§9 #24). Der Echo-Satz nennt den
  // Begriff und ist nur dann richtig, wenn die SUCHE die einzige Einengung ist;
  // engt auch ein Filter ein, nennt der Satz die Handlung („Clear all filters").
  // 🔴 Die Vorgabe des Status zählt NICHT als Einengung: sie ist der
  // Ruhezustand, und ohne diese Zeile trüge der Echo-Satz nie.
  const nurSucheEngt = Boolean(state.q)
    && !categoryFilter && !productTypeFilter && quickListe.length === 0
    && statusIstStandard && !skuFilterSet;

  const emptyState = leerGrund === 'error' ? (
    <EmptyState
      reason="error"
      message={overviewError || recSummaryError}
      action={<Button size="small" variant="outlined" onClick={refreshAll}>Try again</Button>}
    />
  ) : leerGrund === 'no-data' ? (
    <EmptyState
      reason="no-data"
      message="No products in the catalogue yet."
      action={<Button size="small" variant="outlined" onClick={refreshAll}>Refresh</Button>}
    />
  ) : nurSucheEngt ? (
    // 🔴 `noun` ist EINZAHL (§9 #23) — der Echo-Satz lautet „No product matches
    // “x”."; der ZÄHLER daneben trägt den Plural („products").
    <EmptyState reason="no-match" query={search.trim()} noun="product" />
  ) : (
    // 🔴 §8.1 / Karte G1 §13.1 (Nachbesserung G1a, 2026-10-14, R16 B4): ist an
    // der leeren Liste eine VORGABE beteiligt, nennt der Leerzustand sie — und
    // bietet sie als EIGENEN Ausweg an („Resolved cases are hidden." / „Show
    // resolved cases" ist das Muster). Hier ist die Status-Vorgabe
    // (`ACTIVE_STATUSES`, §2.2) der Regelfall: sie ist der Ruhezustand, und
    // „Clear all" BEHÄLT sie (§4.3 — „Abfrage → Grundzustand, Vorgaben bleiben,
    // als Chip sichtbar"). Steht die Liste wegen der Vorgabe leer, ist „Clear all
    // filters" also die FALSCHE Handlung — ohne den zweiten Knopf führte der
    // einzige angebotene Weg in denselben leeren Zustand zurück.
    <EmptyState
      reason="no-match"
      message="No products match the current filters."
      // 🔴 R16 W1 (Nachbesserung G1a Runde 2, 2026-10-14): der Satz nennt die
      // VORGABE, nicht ihren MECHANISMUS. Bis hierher stand „… hidden by the
      // default status filter." — „default status filter" ist Mechanik-Vokabular,
      // und das Hausvorbild (`RMACasesDashboardBody.jsx` › `resolvedAll && onShowResolved`) sagt schlicht
      // „Resolved cases are hidden." Der Knopf daneben ist die Antwort darauf.
      // 🔴 **Kopfvermerk (G1c-2 NB3, 2026-10-15).** Der Zeiger `:544` traegt
      // nicht — und hat auch VOR dem Merge nicht getragen. Der Merge von
      // `origin/master` hat `RMACasesDashboardBody.jsx` umgebaut (G5a-
      // Nachbesserung 2, Leerzustand): `:544` traf den `§3.7`-Kommentarblock,
      // die gemeinte Meldung gab es in der Datei gar nicht. Sie steht jetzt auf
      // `:675` und lautet „Resolved cases are hidden by default — see the
      // „Resolved: hidden" chip above." Der Satz oben bleibt stehen (Regel 11
      // §A5: eine gealterte Aussage bekommt den Vermerk, nicht die neue Ziffer).
      detail={statusIstStandard
        ? 'Unlisted and archived products are hidden by default.'
        : undefined}
      action={(
        <Box sx={{ display: 'flex', gap: SP.tight, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={z3Query.clearAll}>Clear all filters</Button>
          {statusIstStandard && (
            // `[]` ist der Marker „ausdrücklich ohne Einschränkung" (§10.1):
            // `schreibeWert` schreibt `status=all`, die Leseseite gibt `[]`
            // zurück — und `[]` heisst in `filteredRows` „kein Statuskriterium".
            // 🔴 `variant="text"` — die KNOPFVARIANTE des Hausvorbilds
            // (`RMACasesDashboardBody.jsx` › `resolvedAll && onShowResolved`: „Show resolved cases"
            // `variant="text"`). R16 W1 (Runde 2) hat sie ausdrücklich
            // verlangt; G2a wird auf dieselbe Variante gezogen.
            // 🔴 **Kopfvermerk (G1c-2 NB3, 2026-10-15).** Der Merge von
            // `origin/master` hat diesen Knopf VERSCHOBEN und UMgestellt:
            // „Show resolved cases" steht jetzt auf `:727` mit
            // `variant="outlined"` (G5a). Das Vorbild traegt die Variante damit
            // nicht mehr — die Begruendung fuer `variant="text"` ruht auf
            // R16 W1 (Runde 2) allein. Die Variante selbst bleibt unangetastet
            // (kein Teil dieses Auftrags). Der Satz oben bleibt stehen
            // (Regel 11 §A5: der Fliesstext der Aufzeichnung wird nicht
            // umgeschrieben, die geaenderte Tatsache steht im Vermerk).
            <Button size="small" variant="text" onClick={() => set('status', [])}>Show all statuses</Button>
          )}
        </Box>
      )}
    />
  );

  if (loading) {
    return <ProductsTableSkeleton />;
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <div id="products-table-anchor" />
      <Panel data-testid="liste-panel">
        {/* 🔴 N8.5 (G1a, §2.5): der Fehler des ERSTEN Aufbaus sperrt Suche,
            Facetten, Quick-Filter UND den Filter-Öffner — und der GRUND steht
            sichtbar ÜBER der Leiste. Zwei Aussagen, nicht zweimal dieselbe: der
            `EmptyState` im Tisch nennt, WAS fehlschlug („Products could not be
            loaded."), dieser `Alert` nennt, WARUM die Bedienelemente gesperrt
            sind. 🔴 Der Grund steht NUR hier, nicht im Baustein: die drei
            Bausteine kennen kein `DisabledReason` (es kann eine durchgereichte
            Ursache nicht benennen). */}
        {overviewError && (sperre || pagedRows.length > 0) && (
          <Alert
            severity="warning"
            sx={{ mb: SP.card }}
            // 🔴 Zwei Lagen, zwei Kennungen (Nachbesserung G1a, 2026-10-14,
            // R16 B3): die SPERRE gilt nur beim ersten Aufbau (`sperre`). Danach
            // bleiben die Bedienelemente bedienbar (N4.5) — der Fehler wird aber
            // weiterhin GEMELDET. Ohne diesen zweiten Zweig wäre ein gescheitertes
            // Auffrischen still: die alten Zeilen stehen weiter im Baum, der
            // Zähler verschwindet, und keine Zeile nennt den Grund.
            // 🔴 Das DRITTE GATTER (Nachbesserung G1a Runde 4, 2026-10-14, R16r3
            // W2 / G2a-Hausform `POOverview.jsx` › `auffrischen-gescheitert`): der Satz behauptet
            // SICHTBARE Zeilen („The last loaded rows are still shown"). Trifft
            // der Filter null Zeilen, steht unten der Fehler-Leerzustand — und der
            // Satz wäre falsch. Deshalb nur, wenn die Seite Zeilen trägt
            // (`pagedRows` = die WIRKLICH gezeichneten, §3.1).
            data-testid={sperre ? 'erster-aufbau-gesperrt' : 'aktualisierung-gescheitert'}
          >
            {sperre
              ? 'Search and filters are locked until the list loads. Use Try again below.'
              // 🔴 R16 C1 (Nachbesserung G1a Runde 2, 2026-10-14): der Satz nennt
              // den WIEDERHOLEN-WEG. N4.5 verlangt ihn ausdrücklich („dann mit
              // ihrem gewählten Wert und einem Wiederholen-Weg"); der Weg
              // EXISTIERT (`RefreshButton` in Z1, `aria-label="Refresh products"`,
              // Werkzeugtip „Refresh"), wurde aber nicht genannt — während der
              // Zweig zwei Zeilen darüber seinen Weg nennt. Der Bediener läse
              // „could not refresh" und müsste raten, dass der Knopf unter ihm
              // derselbe Vorgang ist.
              // 🔴 Runde 4: der Wortlaut ist auf die G2a-Hausform gezogen
              // („Use Refresh to try again.", ohne „below"; Orchestrator-Entscheid
              // Runde 4, R16r3 W1 im G2a-Zweig — G2as Fassung gewinnt, G1a zieht
              // nach). Die Angabe „below" war G1as Alleingang.
              : 'Could not refresh the list. The last loaded rows are still shown. Use Refresh to try again.'}
          </Alert>
        )}
        <ListToolbar
          // 🔴 Der Kurzbefehl ist Einzelinstanz (G0.3) — genau EINE Listensuche
          // trägt ihn, und die Leiste verwaltet die Warteliste selbst.
          shortcut="/"
          search={{
            value: search,
            onChange: (v) => set('q', v),
            onCommit: (v) => set('q', v),
            // 🔴 Ein Text in BEIDEN Breiten (N7.4) — die zwei Muster der
            // Vorlage („Search SKU / Name" am Rechner, etwas anderes mobil)
            // waren genau der Verstoss, den P5 misst.
            placeholder: 'Search SKU or name…',
            // P5: der zugängliche Name beginnt mit „Search".
            label: 'Search products',
            // 🔴 P5/P34: die ROLLE des Felds. `SearchTrigger` setzt `type="search"`
            // absichtlich nicht (es änderte die Rolle für alle 16 Bestandsaufrufer)
            // — `inputRole` ist der opt-in, und §15 P5/P16/P23/P34 suchen genau
            // diese Rolle (`selektoren.js` §`suchfeld`).
            inputRole: 'searchbox',
            // N8.5: die Sperre des ersten Aufbaus reist über `{...search}`.
            disabled: sperre,
            // 🔴 N2.9/N7.3 (Nachbesserung G1a Runde 2, 2026-10-14): **die Suche
            // füllt den Rest von Z1.** `ListToolbar` setzt `flex: 1` am
            // umgebenden Kasten, `SearchTrigger` reicht dieses `sx` aber an das
            // `TextField`-WURZELELEMENT weiter (`MuiFormControl-root`) — und DAS
            // trug kein `flex`. Das Feld stand deshalb auf seiner Eigenbreite
            // (~220 px), während sein Kasten schon wuchs: die erste Facette rückte
            // um den Rest nach rechts (Fotobefund 1440, grosse Lücke).
            // Hausform: die Musterroute (`ListenMuster.jsx:595`) und G2a
            // (`POOverview.jsx`) tragen dieselbe Zeile. 🔴 Rein additiv: das `sx`
            // gehört dem AUFRUFER, der Baustein bleibt unberührt.
            sx: { flex: 1, minWidth: 0 },
          }}
          // 🔴 Genau DREI Facetten (§2.2) — die Leiste wirft bei mehr. Desktop
          // als Pillen (`desktopForm="pill"`, BS7); mobil liegen dieselben
          // Kriterien im Blatt (`moreFilters.criteria`), weil Z1 unter `sm`
          // niemals Facetten inline rendert (N11.8).
          facets={[
            <BottomSheetMultiSelect
              key="status"
              desktopForm="pill"
              label="Status"
              value={statusFilter}
              onChange={(v) => set('status', v)}
              options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
              disabled={sperre}
            />,
            <BottomSheetSelect
              key="category"
              desktopForm="pill"
              label="Category"
              value={categoryFilter}
              onChange={(v) => set('category', v || null)}
              clearable
              options={categories.map((c) => ({ value: c, label: c }))}
              disabled={sperre}
            />,
            <BottomSheetSelect
              key="type"
              desktopForm="pill"
              label="Product type"
              value={productTypeFilter}
              onChange={(v) => set('type', v || null)}
              clearable
              options={productTypes.map((p) => ({ value: p, label: getCanonicalTypeDisplayName(p) }))}
              disabled={sperre}
            />,
          ]}
          moreFilters={{
            // N8.5: der Öffner FÜHRT zu den Facetten und sperrt mit.
            disabled: sperre,
            // 🔴 Dieselben drei Kriterien wie die Z1-Facetten (§2.1) — ohne den
            // Eintrag wären sie mobil UNERREICHBAR (die Leiste rendert Facetten
            // nie inline unter `sm`), ein stiller Verlust.
            // 🔴 Der Leerwert heisst „Any" (N11.14, §2.4): „jeder Kriterienwert
            // ohne Öffnen lesbar". P25 verbietet „All"/„Any" als FACETTENOPTION,
            // nicht den angezeigten Blatt-Wert.
            criteria: [
              {
                key: 'status',
                label: 'Status',
                multiple: true,
                value: statusFilter,
                onChange: (v) => set('status', v),
                options: STATUS_OPTIONS.map((s) => ({ value: s, label: s })),
                emptyLabel: 'Any',
              },
              {
                key: 'category',
                label: 'Category',
                clearable: true,
                value: categoryFilter,
                onChange: (v) => set('category', v || null),
                options: categories.map((c) => ({ value: c, label: c })),
                emptyLabel: 'Any',
              },
              {
                key: 'type',
                label: 'Product type',
                clearable: true,
                value: productTypeFilter,
                onChange: (v) => set('type', v || null),
                options: productTypes.map((p) => ({ value: p, label: getCanonicalTypeDisplayName(p) })),
                emptyLabel: 'Any',
              },
              // 🔴 **G1c-2 (§2.2): die zwei neuen Kriterien.** Sie stehen NUR hier
              // (und in `dimensionen`) — nicht in Z1 (die 3er-Grenze) und nicht
              // als Zähler (§2.2: der Baustein traegt keine Zahl, und ein
              // Label-Zusatz liefe in den Z3-Chip).
              // 🔴 `clearable` wird NICHT gesetzt: `FilterSelect` rendert sonst
              // ein „All"-MenuItem (`FilterSheet.jsx:920`) — gegen P25. Das
              // `status`-Kriterium (ebenfalls `multiple`) setzt es aus demselben
              // Grund nicht; zurueckgesetzt wird ueber die Checkboxen bzw. den
              // Z3-Chip.
              // 🔴 Kein `disabled` je Kriterium: die Sperre erbt ueber
              // `moreFilters.disabled` (`N8.5`), keine zweite Sperre (§2.5).
              {
                key: 'vertical',
                label: 'Vertical',
                multiple: true,
                value: verticalFilter,
                onChange: (v) => set('vertical', v),
                options: verticals,
                emptyLabel: 'Any',
              },
              {
                key: 'vendor',
                label: 'Vendor',
                multiple: true,
                value: vendorFilter,
                onChange: (v) => set('vendor', v),
                options: vendors,
                emptyLabel: 'Any',
              },
            ],
            // 🔴 Der Sortier-Abschnitt ist der ERSTE des Blattes (N11.14): die
            // Liste ist sortierbar, und mobil gibt es keinen Tabellenkopf. Drei
            // Schlüssel (§2.4) — Name, Stock-out, Price reco.
            sort: {
              value: sortFeld,
              onChange: (v) => set('sort', v),
              options: [
                { value: 'product_name', label: 'Name' },
                { value: 'stock_out_date', label: 'Stock-out' },
                { value: '-rec_abs_diff', label: 'Price reco' },
              ],
            },
            // 🔴 Eine ZAHL, nicht `null`: „Show results" ohne Zahl liest sich
            // wie ein unbekannter Bestand, und der Bestand ist bekannt.
            resultCount: sortedRows.length,
            activeCount: produktFilterAktiv,
            onClearAll: z3Query.clearAll,
          }}
          // Z2 (§2.2) — die fünf Quick-Pillen, kombinierbar UND verknüpft.
          // 🔴 **OHNE „All"** (N3.2): eine gedrückte Pille = eine Einschränkung,
          // keine gedrückte = keine. `alwaysShow`, weil die Reihe auch am
          // Rechner steht.
          quickFilters={(
            <FilterPillRow
              alwaysShow
              value={quickListe}
              onChange={applyQuickFilter}
              options={QUICK_OPTIONS}
              multi
              disabled={sperre}
            />
          )}
          // 🔴 §4.2/N4.3: GENAU EIN Zähler. Er sitzt am Rechner in Z1 und mobil
          // in Z3 — die Leiste rendert ihn in genau einem der beiden, nie in
          // beiden. `M` ist der Bestand OHNE Vorgaben (er antwortet auf „sehe ich
          // alles?"); N ist die Treffermenge.
          //
          // 🔴 **Kein Zähler, solange die Liste noch NIE aufgebaut wurde** — und
          // nur dann (§4.2: „Fehler | kein Zähler (der Leerzustand spricht)";
          // §8.2: „der Zähler springt nicht auf 0").
          // R16 C2 (Nachbesserung G1a Runde 2, 2026-10-14): bis hierher hing das
          // an `leerGrund === 'error'`. `leerGrund` liest aber `overviewError`,
          // nicht „die Liste ist aufgebaut" — nach einem ERFOLGREICHEN Erstaufbau
          // und einem danach GESCHEITERTEN Abruf bleiben die alten Zeilen stehen
          // (`setRows` läuft nicht), der Leerzustand ist gar nicht gerendert, und
          // der Zähler verschwand trotzdem. Der `Alert` darüber sagt wörtlich
          // „The last loaded rows are still shown." — und die Zahl, die genau das
          // belegte, fehlte.
          // 🔴 **Die Bedingung ist `jeGeladen`, NICHT `sortedRows.length === 0`.**
          // Die zweite Fassung war `sortedRows.length === 0 ? undefined : …` — und
          // sie hätte `P28` auf `/products` ROT gemacht: die Probe vergleicht die
          // Rollen in Z1 zwischen dem Grundzustand und dem Leerzustand (`no-match`
          // über die Suche) und verlangt sie GLEICH (N4.5). Im `no-match` ist
          // `sortedRows.length === 0` — der Zähler wäre dort verschwunden und die
          // beiden Aufnahmen gingen auseinander. `jeGeladen` trennt sauber: es ist
          // `false` NUR, solange noch kein Abruf gelungen ist (erster Aufbau,
          // Fehler) — dort spricht der Leerzustand, und dort fehlt der Zähler.
          // In `no-data`, `no-match` und nach einem gescheiterten Auffrischen ist
          // er `true`, und der Zähler bleibt (das war er auch vor dieser Runde).
          count={(sortedRows.length === 0 && !jeGeladen) ? undefined : { noun: 'products', n: sortedRows.length, m: rows.length }}
          // 🔴 N2.9: „Ansicht/Sortierung" steht NUR ohne Tabellenkopf in Z1
          // (§2.1 5a) — diese Liste HAT einen Tabellenkopf, die Sortierung sitzt
          // also dort. Der Slot bleibt leer.
          refresh={<RefreshButton ariaLabel="Refresh products" onClick={refreshAll} />}
          // Z3 (§2.2) — die Chips und „Clear all". Der SKU-Chip kommt aus der
          // Ansicht dazu (§3.5), deshalb das umgeschlossene `query`.
          query={z3Query}
        >
          {/* 🔴 §2.5: die Empfehlungs-Spalte ist im Fehlerfall UNBEKANNT, nicht
              leer — und der Grund gehört an die Spalte, nicht in eine stille
              Rücklage. Die Zelle trägt „– unknown" (en-Strich, §8.1), dieser
              `Alert` nennt den Grund. Nie eine `0` (Regel 22 Nr. 3). */}
          {recSummaryError && (
            <Alert
              severity="warning"
              data-testid="spalten-grund-preis-empfehlung"
              // 🔴 Kein `mx`: das `Panel` traegt seinen Rand schon selbst
              // (`pad="card"`). Ein zweiter Rand waere die doppelte Einrueckung
              // — die Musterroute braucht `mx`, weil ihr Panel `pad="none"` hat.
              sx={{ mt: SP.card }}
            >
              {`Price recommendations could not be loaded — the “Price reco” column reads “unknown”, not “none”. ${recSummaryError}`}
            </Alert>
          )}

          {/* 🔴 N10.4/P13 — ein UNBEKANNTER Wert in der Adresse wird VERWORFEN
              und EINMAL gemeldet. Der Hook entfernt ihn (`replace`, kein
              Verlaufsschritt) und führt den Grund in `ignoredValues`; ohne diese
              Zeile fiele die Bereinigung still aus, und der Bediener sähe eine
              Liste, die etwas anderes zeigt als seine Adresse behauptet. */}
          {query.ignoredValues.length > 0 && (
            <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.card }}>
              {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
            </Alert>
          )}

        {isMobile ? (
          /* ── Mobile: card list ── */
          /* 🔴 `liste-karte` ist §15-Konvention (die zehn Zonen-Marken,
             `concepts/tabellen-filter-suche.md` §15), KEINE Muster-Eigenheit:
             kein Baustein stempelt sie, die Ansicht setzt sie von Hand — genau
             wie `liste-tabelle`/`liste-fuss` daneben (G2-Grill B2). */
          <Box data-testid="liste-karte" sx={{ mt: 1 }}>
            {pagedRows.length === 0 && emptyState}
            {pagedRows.map((r, idx) => {
              // 🔴 Die `0` bleibt: zwei Zeilen weiter steht `.toFixed(1)`, und
              // `margeProzentVomNetto` gibt ohne Nettopreis `null` zurueck. Das
              // Regel-14-Gate hat genau hier einen Absturz vorhergesagt, waere die
              // Ruecklage mit vereinheitlicht worden (EK-A19, B1).
              const marginPct = margeProzentVomNetto(r.net_list_price, r.cost_per_item) ?? 0;
              const rowTarget = productRowTarget(r.sku);
              const reco = recPill(r.rec_markets, !!recSummaryError);
              const cat = (r.category_abcd || '').toUpperCase();
              const catColor = cat === 'A' ? 'success' : cat === 'B' ? 'info' : cat === 'C' ? 'warning' : 'default';
              const rawStockout = r.stock_out_date || r.stockout_date || r.predicted_stockout_date;
              const stockoutDate = rawStockout ? new Date(rawStockout) : null;
              const stockoutSoon = stockoutDate && isFinite(stockoutDate)
                && (stockoutDate - new Date()) / (1000 * 60 * 60 * 24) <= 14;
              const marginColor = marginPct >= 30 ? 'success.main' : marginPct < 10 ? 'error.main' : 'text.secondary';
              const isBundle = !!r.is_bundle;
              const cardKey = isBundle
                ? `bundle-${r.product_id || r.id}-${page}`
                : `${r.sku || 'row'}-${page}-${idx}`;
              return (
                // 🔴 G1b (§2.4): die handgerollte Karte ist durch den
                // gemeinsamen Baustein ersetzt (N11.1 — „keine Kartenkomponente
                // je Ansicht"). Die Felder sind die des Plans:
                // `leading` Thumb · `title` Name (Link) · `id` SKU · `chips`
                // Status + Kategorie · `meta` Kerndaten · `actions` Price reco.
                // 🔴 `slotOrder="chips-first"` ist die EINE Stelle, an der
                // `/products` N11.2 folgt (Status vor Kerndaten); der Baustein
                // bleibt bei seiner Vorgabe (G1b Commit 1).
                <MobileRecordCard
                  key={cardKey}
                  // Hausform wie `rma/RMAFaelligkeit.jsx:472`
                  // (`faelligkeit-zeile-<nr>`): die Karte ist ueber ihren
                  // Datensatz adressierbar, nicht ueber ihre Rolle.
                  data-testid={`produkt-karte-${r.sku || r.product_id || idx}`}
                  slotOrder="chips-first"
                  // P15 greift ueber diese Marken zu (§15, `SEL.kartenKennung`).
                  // 🔴 `chips` und `meta` tragen ebenfalls Marken: N11.2 ist
                  // eine Aussage ueber die REIHENFOLGE (Status vor Kerndaten),
                  // und die ist ohne zwei adressierbare Plaetze im Browser
                  // nicht messbar.
                  fieldTestIds={{
                    id: `karte-kennung-${r.sku || r.product_id || idx}`,
                    chips: `karte-chips-${r.sku || r.product_id || idx}`,
                    meta: `karte-meta-${r.sku || r.product_id || idx}`,
                  }}
                  sx={{ mb: SP.chip }}
                  leading={<ProductThumb src={r.featured_image_url} alt="" size={40} />}
                  title={
                    // 🔴 Der TITEL traegt den Weg hinein, nicht die Flaeche
                    // (`MobileRecordCard` §@dontUseFor). Ein Bundle oeffnet
                    // weiter das `SlidePanel` — als echter Knopf, nicht als
                    // Klickfaenger auf der Karte.
                    isBundle ? (
                      <Link
                        component="button"
                        type="button"
                        onClick={() => openBundlePanel(r)}
                        underline="hover"
                        color="inherit"
                        sx={KARTE_TITEL_SX}
                      >
                        {/* 🔴 R16 B1: die Zwei-Zeilen-Klemme sitzt am INNEREN
                            `<span>`, nicht am `Link` — sonst schneidet ihr
                            `overflow: hidden` den 44-px-Boden (`::after`) ab
                            (Begruendung an `KARTE_TITEL_SX`).
                            🔴 Die MARKE sitzt am Klemmen-Traeger, nicht am `Link`
                            (Nachbesserung 8, P15): `tests/listen/produkte.spec.ts`
                            liest die Klemme ueber `[data-testid^="karte-titel-klemme-"]`.
                            Der `Link` traegt den 44-px-Boden und damit KEIN
                            `-webkit-line-clamp` — wer die Klemme wieder an ihn
                            zieht, faerbt P15 rot (das ist der Rotweg). */}
                        <Box
                          component="span"
                          data-testid={`karte-titel-klemme-${r.sku || r.product_id || idx}`}
                          sx={TITEL_KLEMME_SX}
                        >
                          {r.product_name || '(no name)'}
                        </Box>
                      </Link>
                    ) : (
                      <Link
                        component={RouterLink}
                        to={rowTarget}
                        underline="hover"
                        color="inherit"
                        sx={KARTE_TITEL_SX}
                      >
                        {/* 🔴 R16 B1: die Zwei-Zeilen-Klemme sitzt am INNEREN
                            `<span>`, nicht am `Link` — sonst schneidet ihr
                            `overflow: hidden` den 44-px-Boden (`::after`) ab
                            (Begruendung an `KARTE_TITEL_SX`).
                            🔴 Die MARKE sitzt am Klemmen-Traeger, nicht am `Link`
                            (Nachbesserung 8, P15): `tests/listen/produkte.spec.ts`
                            liest die Klemme ueber `[data-testid^="karte-titel-klemme-"]`.
                            Der `Link` traegt den 44-px-Boden und damit KEIN
                            `-webkit-line-clamp` — wer die Klemme wieder an ihn
                            zieht, faerbt P15 rot (das ist der Rotweg). */}
                        <Box
                          component="span"
                          data-testid={`karte-titel-klemme-${r.sku || r.product_id || idx}`}
                          sx={TITEL_KLEMME_SX}
                        >
                          {r.product_name || '(no name)'}
                        </Box>
                      </Link>
                    )
                  }
                  // 🔴 N11.5: die Kennung wird NIE gekuerzt (der Baustein traegt
                  // `nowrap` und kein `overflow: hidden`). Bundles ohne SKU
                  // tragen keine — eine erfundene waere schlimmer als keine.
                  id={r.sku || undefined}
                  chips={(
                    <>
                      {/* Dieselbe `Pill` wie am Rechner (R15 W1) — NICHT der
                          rohe kleingeschriebene Status. 🔴 Die Planstelle §2.4
                          („der Status-Chip traegt den Rohwert") beschreibt den
                          Stand VOR G1a; G1a hat ihn mit R15 W1 auf `StatusPill`
                          gezogen, und N11.3 verlangt dieselbe Variante wie die
                          Tabelle. */}
                      <StatusPill row={r} />
                      {/* 🔴 D-3a (R16 C1): dieselbe Ausnahme-Marke wie in der
                          Namenszelle der Tabelle — „beide Breiten, eine Form"
                          (`StatusPill`-Muster). Die frueher hier stehende
                          `meta`-Zeile „Vertical · …" ist damit entfallen: sie
                          war die zweite Form desselben Werts, und `brewout`
                          (92 % des Bestands) braucht auch auf der Karte keine
                          eigene Zeile. */}
                      <VerticalMark row={r} />
                      {cat && (
                        <Chip
                          size="small"
                          label={cat}
                          color={catColor}
                          variant={catColor === 'default' ? 'outlined' : 'filled'}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                      {isBundle && (
                        <Tooltip title="Bundle — view components">
                          <Chip size="small" label="Bundle" variant="outlined" sx={(t) => ({ color: v2Tone(t, 'info').main, borderColor: v2Tone(t, 'info').main, height: 20, fontSize: 11 })} />
                        </Tooltip>
                      )}
                    </>
                  )}
                  meta={(
                    <>
                      {/* N11.3 — „nichts geht verloren": die Variante stand in
                          der alten Karte unter dem Namen und steht hier im
                          Kerndatenblock. */}
                      {r.variant_name && r.variant_name !== r.product_name && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {r.variant_name}
                        </Typography>
                      )}
                      {/* 🔴 **G1c-2 (§2.3, sechstes Ziel): die Marke in der
                          mobilen Karte.** Ohne diesen Eintrag verschoende die
                          neue Spalte auf dem TELEFON — gegen N11.3 („nichts geht
                          verloren"). Die Karte traegt keine Spaltenstufen, der
                          Eintrag gilt hier IMMER (nicht `sichtbar.has`).
                          Link nur mit `vendor_id`, sonst der blosse Text; ohne
                          Marke steht hier NICHTS (kein `—`, wie in der
                          Rueckfallebene der Tabellenzelle). */}
                      {r.brand && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {r.vendor_id ? (
                            <Link
                              component={RouterLink}
                              to={`/vendors/${r.vendor_id}`}
                              underline="hover"
                              color="inherit"
                              // 🔴 R16 W4: der Name steht bei `noWrap` gekuerzt
                              // im Kasten — vollstaendig im `title`, genau wie
                              // im Text-Zweig darunter (ohne `vendor_id`,
                              // `ProductsTable.jsx:2904`).
                              // 🔴 **Die Ziffern sind am ENDSTAND nachgeschlagen, nicht
                              // gerechnet (R15 W-2 der Runde 5, R16 B1 der Runde 6,
                              // 2026-10-16):**
                              // `grep -n "title={r.brand}"` findet SIEBEN Stellen — drei sind gemeint:
                              // `:2265` (dieser Karten-Link), `:2839` (ein Kommentar)
                              // und `:2904` (`<Typography variant="body2" noWrap
                              // title={r.brand} …>`, der Text-Zweig der
                              // Brand-Spalte). Hier stand bis zum 2026-10-16
                              // „`:2462`" — eine Zahl, die auf keine der drei traf.
                              // ⚠️ Nachgezaehlt am Endstand (R16 W3 der Runde 7, 2026-10-16): SIEBEN Treffer — drei sind Kommentare,
                              // und zwar `:2230`, `:2233` und `:2839` — die ersten zwei liegen DREI Zeilen auseinander, nicht benachbart.
                              // ⚠️ **Der Dateiname steht daneben, und er macht den
                              // Zeiger NICHT waechter-sichtbar — gemessen:**
                              // `check_doc_references.py` meldet vor und nach dieser
                              // Zeile **6100** Verweise (er liest `.md`),
                              // `check_verweis_grundlinie.py` filtert ebenso. Der
                              // Name hilft dem naechsten Menschen und dem
                              // Vollstaendigkeits-Grep, nicht der CI.
                              // 🔴 **R16 B1 der Runde 6 (2026-10-16): „NICHT wie in
                              // der Tabellenzelle" ist ueberholt.** Hier stand, der
                              // LINK-Zweig der Zelle habe den `title` in der
                              // Nachbesserung 4 verloren, „damit genau EINE
                              // Sprechblase steht (R16 B2)". **Seit der
                              // Nachbesserung 8 traegt der Band-Link den `title`
                              // wieder** (`:2751`, Begruendung an der
                              // Aufrufstelle): die Marke kuerzt sich seither selbst,
                              // und der volle Name waere ohne ihn nicht mehr
                              // aufzuloesen. Der **Spalten**-Link traegt ihn
                              // weiterhin NICHT — dort steht das MUI-Popup
                              // („Open vendor page", `:2797`).
                              // ⚠️ **„Genau EINE Sprechblase" gilt trotzdem, je
                              // Zeile:** das Band-Fragment haengt an
                              // `!sichtbar.has('brand')`, die Spalte samt Popup an
                              // `sichtbar.has('brand')` — Negation desselben
                              // Praedikats, die beiden schliessen einander aus. In
                              // einer Zeile steht damit genau EIN Muster; hier
                              // stand bis zum 2026-10-16 das Gegenteil
                              // (R15 W-2 der Runde 6).
                              title={r.brand}
                              sx={{ display: 'block', ...MARKE_KARTE_LINK_SX }}
                            >
                              {/* 🔴 R16 B1: der Boden liegt auf dem `Link`, die
                                  Klemme auf einem INNEREN `<span>` — sonst
                                  schnitte ihr `overflow: hidden` den 44-px-Boden
                                  ab (Begruendung an `MARKE_KARTE_LINK_SX`).
                                  Dieselbe Bauart wie der Kartentitel
                                  (`TITEL_KLEMME_SX`). 🔴 **Die KARTE traegt 44,
                                  die Tabellenzelle 24** — zwei Boeden aus zwei
                                  Tokens (`DICHTER_TEXT_LINK_SX`). */}
                              <Box component="span" sx={MARKE_KLEMME_SX}>{r.brand}</Box>
                            </Link>
                          ) : (
                            <Box component="span" sx={MARKE_KLEMME_SX}>{r.brand}</Box>
                          )}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Stock: <b>{r.current_inventory ?? '—'}</b>
                          {r.incoming_inventory > 0 && ` (+${r.incoming_inventory})`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          30d: <b>{formatNumber(r.average_sales_30d || 0, { digits: 1 })}</b>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Price: <b>{fmtCurrency(r.list_price)}</b>{' · '}
                          <Box component="span" sx={{ color: marginColor }}>
                            {formatPercent(marginPct / 100, { digits: 1 })}
                          </Box>
                        </Typography>
                      </Stack>
                      {stockoutSoon && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.25 }}>
                          Stock-out {formatDate(stockoutDate)}
                        </Typography>
                      )}
                    </>
                  )}
                  // Price reco — dieselbe Aussage wie die Desktop-Spalte (§4.4):
                  // Betrag + Markt statt eines gruenen Bits.
                  actions={<Pill tone={reco.tone}>{reco.label}</Pill>}
                />
              );
            })}
          </Box>
        ) : (
          /* ── Desktop: full table ──
              🔴 **Kein `maxHeight` mehr** (Owner-Entscheid 2026-10-14, B3:
              „Pagination begrenzt die Einträge, die Tabelle bekommt den Platz,
              den sie auf der Seite braucht"). Der Deckel war `calc(100vh -
              220px)` — und die 220 waren eine ANNAHME ueber die Chrome-Hoehe
              darueber, die schon einmal falsch war (W-39: zwei
              Dashboard-Widgets, dann eine eingeklappte `CollapsibleSection`).
              Ein geschaetzter Deckel, der die Tabelle beschneidet, ist genau
              die Sorte Zahl, die still veraltet.

              Die Zeilenzahl begrenzt jetzt die Pagination (50, `TableFooterSimple`
              weiter unten), nicht ein Deckel.

              ⚠️ **Der Kopf bleibt nur AB `lg` sichtbar.** Dort steht der KASTEN auf
              `overflow: visible`; damit ist die SEITE der Rollbehaelter und
              `position: sticky` greift ueber ihr. **Unter `lg` klebt er NICHT:**
              der Kasten ist dann ein Scroll-Container OHNE Hoehe, scrollt
              senkrecht also nie, und der Kopf laeuft mit der Seite weg. Das ist
              die Hausform (`pages/SecondLifeBestand.jsx:2287-2290` sagt es
              ausdruecklich) und kein Versehen. */
          /* 🔴 `liste-tabelle` ist §15-Konvention und wird von KEINEM Baustein
             gestempelt (G2-Grill B2, am Kode gemessen) — die Ansicht setzt sie
             von Hand. Sie liegt AUSSERHALB von `TableViewport`: die Zone ist der
             Tabellenkörper-Träger, nicht der Rollbehälter. */
          /* 🔴 G1b: der `ref` misst die **Container**-Breite (BW4) — den RAUM,
             nicht die gedeckelte Tabelle. Er sitzt deshalb HIER und nicht am
             `TableViewport` (sonst mässe sich der Deckel selbst,
             `ListenMuster.jsx:798-803`). */
          <Box data-testid="liste-tabelle" ref={tabelleRef}>
          <TableViewport
            footerPadPx={0}
            // 🔴 Die MELDUNG (BW6): `priorities` schaltet den Kanal ein (Kopf-
            // Deckel + `searchMax`), die rohe Behälterbreite reist als
            // `containerWidth` mit. Beide sind opt-in; ohne sie ist alles wie
            // vorher.
            priorities={SPALTEN}
            containerWidth={behaelterBreite}
            // 🔴 N9.6 — klebende Kennung + Kantenhinweis, NUR im Rollfall. Die
            // zwei gehören zusammen (`TableViewport` unterdrückt bei
            // `stickyFirstColumn` die LINKE Maske, weil die klebende Spalte
            // vollständig sichtbar ist).
            stickyFirstColumn
            edgeHint
            // 🔴 N9.5 — `tableLayout: 'fixed'` mit erklärten Breiten; die
            // Breiten kommen aus `columns` (Anteil des Budgets). Das ANWENDEN
            // der Tabellenbreite = min(Behälter, Budget) ist Sache des
            // Verbrauchers (§4.1:568) — `TableViewport` meldet nur (PW4/PW4b).
            columns={spaltenBreiten}
            sx={{
              mt: 2,
              overflow: { xs: 'auto', lg: 'visible' },
              '& .MuiTableCell-stickyHeader': {
                backgroundColor: (t) => `${t.palette.background.paper} !important`,
                zIndex: 6,
                top: { xs: 0, lg: 64 },
              },
              ...(tabellenMax > 0 ? { maxWidth: tabellenMax } : {}),
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                // 🔴 G1b-Nachbesserung 1 (Befund 1): `minWidth` folgt jetzt den
                // SICHTBAREN Spalten (Σ `COLUMN_MAX[kind].min`), nicht mehr einem
                // festen 880. Vorher erzwang die Konstante bei 1280 eine 880-px-Tabelle
                // in einem auf 680 gedeckelten Kasten — `List` war angeschnitten.
                // Die Tabelle passt damit auf ein 1100-px-Notebook OHNE Querscrollen
                // (Konzept §4.2), und unter Σmin greift N9.6 (klebende Kennung).
                minWidth: tabellenMin,
                // 🔴 KEIN eigenes `borderCollapse: 'separate'` hier (Nachbesserung
                // 5, 2026-10-18). Die Zeile stand bis hier und machte die
                // PW6d-Probe auf `/products` BLIND: der Rotweg entfernt
                // `borderCollapse` aus `TableViewport.stickySx`, die lokale
                // Kopie hielt das Modell aber weiter — die Probe blieb gruen,
                // obwohl die Mutation wirkte. Das Rahmenmodell kommt aus
                // `TableViewport` (dort mit `border-spacing: 0`, das hier
                // fehlte); eine zweite Quelle fuer dieselbe Regel ist genau
                // die Sorte Kopie, die eine Probe unter ihrer eigenen Mutation
                // gruen laesst.
                '& thead th': {
                  bgcolor: (theme) => theme.palette.grey[100],
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.4,
                  fontSize: 12,
                },
                // 🔴 **Diese Regel GEWINNT gegen die des Behaelters** (Regel-16-
                // Befund B1, 2026-10-19): beide haben dieselbe Spezifitaet
                // (0,2,0), und die Regel der KIND-Komponente wird spaeter ins
                // Stylesheet eingefuegt. Ein `top: 0` hier ueberschrieb deshalb
                // das `top: 64` des `TableViewport` — der Kopf klebte bei y=0,
                // also UNTER der festen 64-px-App-Leiste, und war beim Scrollen
                // unsichtbar. Der Versatz gehoert hierher, nicht nur an den
                // Behaelter.
                //
                // `xs: 0` bleibt richtig: unter `lg` ist der Kasten ein
                // Scroll-Container ohne Hoehe — er scrollt senkrecht nie, der
                // Kopf klebt dort nicht und laeuft mit der Seite weg (Hausform,
                // `SecondLifeBestand.jsx`).
                //
                // Opaque bg mit `!important` — MUI's Vorgabe nutzt
                // `background.default` und liest sich gegen das Paper transparent.
                '& .MuiTableCell-stickyHeader': {
                  top: { xs: 0, lg: 64 },
                  zIndex: 6,
                  backgroundColor: (theme) => `${theme.palette.grey[100]} !important`,
                },
                '& .MuiTableCell-root': {
                  py: 1,
                  px: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {sichtbarerKopf.map((h) => (
                    <TableCell
                      key={h.id}
                      align={h.numeric ? 'right' : 'left'}
                      // 🔴 G1b-Nachbesserung 1 (Befund 2): der Kopf traegt `nowrap` und
                      // eine aus dem Budget abgeleitete Breite — wird er sichtbar gekuerzt
                      // (bei 1440 war das `Margin % (of net)`), braucht der Bediener einen
                      // Weg zum VOLLEN Namen (Konzept: „der zugängliche Name bleibt
                      // `Margin % (of net)`", EK-A19). `title` ist dieser Weg; der
                      // zugängliche Name bleibt unveraendert der `TableSortLabel`-Text.
                      // `undefined` fuer die JSX-Beschriftung (Kategorie traegt ihren
                      // eigenen Tooltip) — `title` erwartet eine Zeichenkette.
                      title={typeof h.label === 'string' ? h.label : undefined}
                      // 🔴 G1b-Nachbesserung 2 (Fotobefund 3): trägt die Spalte
                      // eine kürzere SICHTBARE Fassung (`kurz`), bleibt der
                      // zugängliche Name der volle (`label`, EK-A19). Ohne diese
                      // Zeile läse ein Screenreader die Abkürzung und
                      // `margen-zwei-skalen.spec.ts` A19.1 fiele.
                      // 🔴 **R16 W2 (Nachbesserung 3, 2026-10-14): der KNOPF traegt
                      // denselben vollen Namen.** Vorher las ein Screenreader die
                      // Spalte lang („Margin % (of net)") und den Knopf darin kurz
                      // („Margin % (net)") — zwei Namen fuer dieselbe Sache.
                      // 🔴 **R16 W1 (Nachbesserung 5, 2026-10-15): die Zeile bleibt —
                      // und die Begruendung ist jetzt GEMESSEN, nicht behauptet.**
                      // Der Einwand der Runde 2 lautete, die Accname-Rechnung ziehe
                      // den `aria-label` eines Nachfahren-Widgets sehr wohl in den
                      // Namen der Kopfzelle (W3C-Schritt 2F) — dann waere die Zeile
                      // redundant. Gegengeprueft, wie vorgeschlagen: die Zeile
                      // entfernt, `G1b-B3` gefahren → **rot** an genau
                      // `getByRole('columnheader', { name: 'Margin % (of net)' })`.
                      // Nachgemessen mit `computeAccessibleName` (dieselbe Rechnung
                      // wie die Probe): ohne die Zeile ist der Name der Kopfzelle
                      // `"Margin % (net)"` — die SICHTBARE Kurzform, nicht der
                      // `aria-label` des Knopfes. Die Zeile ist damit NICHT redundant.
                      // ⚠️ Was die Messung NICHT belegt: dass ein echter Browser oder
                      // ein Screenreader dieselbe Rechnung fahrt — gemessen ist der
                      // Algorithmus der Probe (Regel 17 §2). Beide tragen ihn deshalb:
                      // die Spalte behaelt ihren Namen, und der Knopf widerspricht
                      // ihm nicht mehr.
                      {...(h.kurz ? { 'aria-label': h.label } : {})}
                      // 🔴 P18/W1 (Nachbesserung G1a, 2026-10-14): MUI schreibt
                      // `aria-sort` auf das `<th>` NUR aus `TableCell.sortDirection`
                      // — `TableSortLabel` allein tut es NICHT. Ohne diese Prop
                      // trug KEINE der acht sortierbaren Spalten eine Auskunft für
                      // Hilfsmittel (WCAG 1.3.1/4.1.2). `false` = „diese Spalte ist
                      // nicht die Sortierspalte", nicht `undefined` (MUI liesse
                      // `undefined` als „kein aria-sort" stehen — dieselbe Wirkung,
                      // aber unbenannt).
                      sortDirection={orderBy === h.id ? order : false}
                    >
                      {h.id !== 'recs' ? (
                        <TableSortLabel
                          active={orderBy === h.id}
                          direction={orderBy === h.id ? order : 'asc'}
                          onClick={() => handleRequestSort(h.id)}
                          // 🔴 R16 W2: der VOLLE Name am Knopf — DERSELBE wie am
                          // `<th>` (Begruendung dort). `TableSortLabel` reicht
                          // unbekannte Props an sein `ButtonBase`-Wurzelelement
                          // weiter, `aria-label` landet also auf dem Knopf. Nur
                          // gesetzt, wenn die sichtbare Fassung abkuerzt — sonst
                          // traegt der Knopf seinen Text ohnehin selbst.
                          {...(h.kurz ? { 'aria-label': h.label } : {})}
                        >
                          {h.kurz ?? h.label}
                        </TableSortLabel>
                      ) : (
                        h.kurz ?? h.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r, idx) => {
                  // 🔴 `?? 0` wie oben — darunter steht `.toFixed(1)` (EK-A19, B1).
                  const marginPct = margeProzentVomNetto(r.net_list_price, r.cost_per_item) ?? 0;
                  const rowTarget = productRowTarget(r.sku);
                  const reco = recPill(r.rec_markets, !!recSummaryError);
                  // Shopify native bundles have no SKU; they open a components
                  // SlidePanel (Phase 2) keyed on product_id. Use product_id as
                  // the row key so deduped, NULL-SKU bundle rows get a stable,
                  // collision-free key.
                  const isBundle = !!r.is_bundle;
                  const rowKey = isBundle
                    ? `bundle-${r.product_id || r.id}-${page}`
                    : `${r.sku || 'row'}-${page}-${idx}`;
                  const clickable = isBundle || !!r.sku;
                  const openRow = () => {
                    if (isBundle) openBundlePanel(r); else if (r.sku) navigate(rowTarget);
                  };
                  const rawStockout = r.stock_out_date || r.stockout_date || r.predicted_stockout_date;
                  const stockoutDate = rawStockout ? new Date(rawStockout) : null;
                  const stockoutSoon = stockoutDate && isFinite(stockoutDate)
                    && (stockoutDate - new Date()) / (1000 * 60 * 60 * 24) <= 14;
                  const rawReorder = r.reorder_date || r.next_reorder_date || r.purchase_order_date;
                  const reorderDate = rawReorder ? new Date(rawReorder) : null;
                  const marginColor = marginPct >= 30 ? 'success.main' : marginPct < 10 ? 'error.main' : 'text.secondary';
                  return (
                    /* 🔴 Die GANZE Zeile ist das Ziel, nicht die erste Zelle.
                       Vorher sass der Handler auf `<TableCell>` Nr. 1 — 12 der 13
                       Spalten waren fuer die Maus tot, und der Klickbereich war
                       rund 300 px einer 1100 px breiten Tabelle. `role`,
                       `tabIndex` und `onKeyDown` ziehen mit hoch: sie waren
                       schon geschrieben, sie standen nur eine Ebene zu tief. */
                    <TableRow
                      key={rowKey}
                      hover
                      onClick={openRow}
                      {...(clickable && {
                        /* 🔴 KEIN `role="button"` auf dem `<tr>`. Der Umbau
                           hob es von der ersten Zelle auf die ZEILE — mit der
                           Absicht „die ganze Zeile ist das Ziel", aber mit einer
                           Nebenwirkung, die die Absicht zerstoert: ein
                           `<tr role="button">` ist keine Tabellenzeile mehr.
                           Die Zeilenstruktur faellt aus dem Barrierefreiheits-
                           baum (`getByRole('row')` findet nichts), und der
                           echte `<a>` im Namensfeld saesse in einem Knopf —
                           verschachtelte Bedienelemente, die ein Screenreader
                           nicht beide erreicht. Die Zeile bleibt eine ZEILE:
                           klickbar (`onClick`), per Tastatur bedienbar
                           (`tabIndex`/`onKeyDown`), und der Link im Namensfeld
                           traegt den zugaenglichen Namen und den ⌘-Klick. */
                        tabIndex: 0,
                        onKeyDown: (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openRow();
                          }
                        },
                      })}
                      sx={{ cursor: clickable ? 'pointer' : 'default' }}
                    >
                      {/* 🔴 G1b (BW5): jede Zelle fragt dieselbe Menge wie der
                          Kopf. Ohne diese Bedingung stünde die Zelle ohne ihren
                          Kopf in der Zeile — die Spaltenzahl wäre konstant und
                          PW5 nicht messbar. */}
                      {sichtbar.has('product_name') && (
                      <TableCell
                        // 🔴 **M2g (2026-11-04): hier stand `verticalAlign: 'top'`
                        // — wieder entfallen, die Hypothese war widerlegt.**
                        //
                        // Die Herleitung war: `middle` zentriert den Zellinhalt,
                        // wenn eine ANDERE Zelle die Zeile höher zieht, und der
                        // Band-Boden wandert um die halbe Höhendifferenz nach
                        // unten. Der Lauf danach (M2g, 1024, grober Zeiger) blieb
                        // **rot an derselben Zeile** — also trägt die Ausrichtung
                        // den Befund nicht.
                        //
                        // Gemessene Ursache ist eine andere: der **Padding-Kasten
                        // der Band-Klemme** ragte 15 px unter die Zeilenkante
                        // (`MARKE_BAND_KLEMME_SX`, Begründung dort) und traf den
                        // Namensstreifen der Folgezeile — unabhängig davon, wie
                        // der Inhalt in der Zelle ausgerichtet ist. Mit der
                        // Klemme am Boden endet der Kasten in der eigenen Zeile,
                        // und die Zelle bleibt beim Tabellen-Standard `middle`.
                        //
                        // ⚠️ **Rest, benannt:** `middle` verschiebt den Inhalt
                        // weiterhin um `d/2`, wenn eine Nachbarzelle um `d` höher
                        // ist. Der Abstand Boden→Zeilenkante ist jetzt ~16 px
                        // statt ~1 px; erst ab `d > 32` wäre er aufgebraucht.
                        // *Widerlegt hätte es:* eine rote Zeile mit `d > 32` —
                        // dann müsste `top` zurück.
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ProductThumb src={r.featured_image_url} alt="" size={28} />
                        <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {/* 🔴 Ein ECHTER Link, nicht nur ein onClick: erst damit
                              gehen ⌘-Klick, Mittelklick und „Link kopieren" — und
                              der Zeilen-Handler wird angehalten, damit der Klick
                              nicht zweimal navigiert. */}
                          {r.sku ? (
                            <Link
                              component={RouterLink}
                              to={rowTarget}
                              underline="hover"
                              color="inherit"
                              onClick={(e) => e.stopPropagation()}
                              // 🔴 G1b-Nachbesserung 2 (Fotobefund 4): der Titel
                              // ist am Rechner auf ZWEI Zeilen geklemmt (§9.1,
                              // N11.5); der volle Name steht im `title` und im
                              // Detail. Ohne die Klemme standen bei 1280/1440
                              // DREI Zeilen (Fotos `1280_products`, `1440_products`).
                              title={r.product_name || '(no name)'}
                              sx={TITEL_KLEMME_SX}
                            >
                              {r.product_name || '(no name)'}
                            </Link>
                          ) : (
                            <Box component="span" title={r.product_name || '(no name)'} sx={TITEL_KLEMME_SX}>
                              {r.product_name || '(no name)'}
                            </Box>
                          )}
                          {isBundle && (
                            <Tooltip title="Bundle — view components">
                              <Chip size="small" label="Bundle" variant="outlined" sx={(t) => ({ color: v2Tone(t, 'info').main, borderColor: v2Tone(t, 'info').main, height: 20, fontSize: 11 })} />
                            </Tooltip>
                          )}
                          {/* 🔴 Der Status, den die Spalte bis hier NIE trug (§4.2
                              hat sie geschnitten — „der Standardfilter IST bereits
                              active+draft"). Seit `unlisted`/`archived` erreichbar
                              sind, ist das nicht mehr wahr: ohne diesen Chip sieht
                              man in einer Liste aus draft/unlisted/archived nicht,
                              WELCHER Status an der Zeile steht. Nur für
                              `!== 'active'` — aktive Zeilen bleiben ruhig, und
                              genau die Status, um die es geht, werden markiert.
                              Ableitung aus `utils/productStatus`, derselben Quelle
                              wie die Detailseite (kein zweites Etikett). Die
                              Form steht in `StatusPill` — mobil und am Rechner
                              EINE (R15 W1 = R16 W1). */}
                          <StatusPill row={r} />
                          {/* 🔴 D-3a (R16 C1): die Vertikale als Ausnahme-Marke
                              — nur wenn sie NICHT `brewout` ist oder fehlt.
                              Dieselbe `Pill` wie mobil („beide Breiten, eine
                              Form"); `brewout` (92 % des Bestands) bleibt
                              unmarkiert. 🔴 **Sie ist die EINZIGE Anzeige in der
                              Tabelle** — die `xl`-Spalte ist am 2026-11-04
                              entfallen (Regel 8, Breitenbudget); hier stand bis
                              dahin „die xl-Spalte traegt den Wert auf breiten
                              Schirmen". */}
                          <VerticalMark row={r} />
                        </Typography>
                        {/* Variante in DIESELBE Zeile wie die SKU gezogen, Thumbnail
                            von 36 auf 28 px: die Zeile soll ~44 px hoch werden,
                            nicht ~52. Die mobile Karte traegt bei weniger Hoehe
                            mehr Information — das war die Messlatte (§4.3). */}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          data-testid={`band-2-${r.sku || r.product_id || idx}`}
                          sx={MARKE_BAND_KLEMME_SX}
                        >
                          {/* 🔴 **Der VORDERTEIL kuerzt sich, die Marke nicht**
                              (Nachbesserung 7, 2026-10-16) — Begruendung an
                              `MARKE_BAND_KLEMME_SX`. Der volle Text steht im
                              `title` (Hausform der gekuerzten Texte: der
                              Namens-Link darueber traegt denselben). */}
                          <Box
                            component="span"
                            title={markeBandVordertext(r, isBundle)}
                            sx={MARKE_BAND_VORDERTEIL_SX}
                          >
                            {markeBandVordertext(r, isBundle)}
                          </Box>
                          {/* 🔴 **G1c-2 / OE-G1c-4a: die Rueckfallebene.** Ist die
                              Brand-Spalte NICHT sichtbar (bei 1024/1280/1440 —
                              §2.3a rechnet es vor), tritt die Marke DIESER
                              bestehenden zweiten Zeile bei — als weiteres Glied
                              hinter SKU/Variante, dasselbe `caption`/
                              `text.secondary`, dieselbe Zelle, dieselbe
                              Daempfung.
                              🔴 **Kein drittes Band:** die Zelle hat ein
                              geschriebenes Hoehenbudget (~44 px, s.o.), und ein
                              eigenes Band fuer die Marke risse es (der Widerleger
                              des Entscheids, §6 O-4). Genau deshalb haengt der
                              Zusatz an `!sichtbar.has('brand')`.
                              🔴 **Keine Doppelanzeige:** ist die Spalte sichtbar,
                              entfaellt der Zusatz hier vollstaendig (Probe `J-B6`).
                              🔴 **Rueckfall „nichts anhaengen"** bei fehlender
                              Marke — kein Trenner, kein `—`: in einer SKU-Zeile
                              waere ein `—` eine Falschaussage ueber die SKU. Das
                              ist die EINE inhaltliche Abweichung von OE-G1c-4 und
                              ausdruecklich gebilligt (§2.3a).
                              🔴 Der Link traegt KEINEN Tooltip: die Zeile ist eine
                              dichte Beischrift, und der Tooltip der Spalte
                              („Open vendor page") waere hier ein zweites Muster.
                              Gepinnt wird Rolle + `href` (`J-B5`).
                              🔴 **Nachbesserung 8 (2026-10-16, R16 B1/W2 der
                              Runde 5): der `title` ist trotzdem zurueck — als
                              Rueckfall der gekuerzten MARKEN.** Die Marke kann
                              sich seit dieser Nachbesserung selbst kuerzen
                              (`MARKE_BAND_KUERZUNG_SX`); ohne `title` waere ein
                              Rueckfallname ueber der Bandbreite nur noch ueber
                              den Zeilenklick zu erfahren. Der Auftrag nennt den
                              `title` ausdruecklich an BEIDEN Zweigen.
                              ⚠️ **Kein zweites Muster in derselben Zeile** (R15 W-2
                              der Runde 6, 2026-10-16): hier stand, es staenden
                              „zwei Sprechblasen-Muster nebeneinander" (nativ
                              hier, MUI-Popup an der Brand-Spalte). Das kann per
                              Konstruktion nicht eintreten — dieses Fragment haengt
                              an `!sichtbar.has('brand')`, die Spalte samt Popup an
                              `sichtbar.has('brand')`, beide mit je genau EINER
                              Aufrufstelle. Je Zeile steht genau EIN Muster. */}
                          {!sichtbar.has('brand') && r.brand && (
                            <>
                              {/* Der Trenner steht VOR der Marke und in einem
                                  EIGENEN Glied — Begruendung an
                                  `MARKE_BAND_TRENNER_SX` (im Flex-Kontext
                                  faellt ein nackter Textknoten als eigenes
                                  anonymes Glied auseinander). */}
                              <Box component="span" sx={MARKE_BAND_TRENNER_SX}>{' · '}</Box>
                              {r.vendor_id ? (
                                <Link
                                  component={RouterLink}
                                  to={`/vendors/${r.vendor_id}`}
                                  underline="hover"
                                  color="inherit"
                                  onClick={(e) => e.stopPropagation()}
                                  // 🔴 **Der NORM-Boden 24, auf JEDEM Zeiger
                                  // (R16 B1 der Runde 3, Nachbesserung 5,
                                  // 2026-10-16).**
                                  //
                                  // Hier stand seit der Nachbesserung 4 **gar
                                  // kein** Boden, mit der Begruendung, der dichte
                                  // Kontext habe im Haus ohnehin keinen
                                  // (`TOUCH_TARGET_COARSE_OFF`). Die Begruendung
                                  // war falsch (R15 W-1 = R16 B1 der Runde 3):
                                  // der Token steht an den vier KNOPF-Wurzeln
                                  // (`theme.js:697` `MuiButton`, `:860`
                                  // `MuiToggleButton`, `:973` klickbarer `Chip`,
                                  // `:1113` `MuiIconButton`) — an einem `Link`
                                  // gibt es nichts zurueckzunehmen, `grep MuiLink
                                  // theme.js` findet 0. Die Auslassung liess den
                                  // Link damit unter der Norm (2.5.8, 24 px), die
                                  // der Orchestrator hier ausdruecklich halten
                                  // will: **Norm statt Auslegung.**
                                  //
                                  // 🔴 Die 44 der Karte passen hier NICHT: die
                                  // Zeile endet **25 px** unter dieser Oberkante
                                  // (gemessen 2026-10-16, M2g-Diagnose), ein
                                  // 44er-Boden hat dort keinen Platz. Die 24
                                  // passen: sie enden in der eigenen Zeile.
                                  //
                                  // 🔴 **Und sie nehmen der Folgezeile nichts —
                                  // korrigiert am 2026-10-16 (R15 W-1, gemessen).**
                                  // Hier stand „dort verliert der Namens-Link des
                                  // Nachbarn das Hit-Testing (R15 B-1)". Mit einem
                                  // 44er-Boden gemessen (Diagnose D44): der Punkt
                                  // `+25` gehoert dem Link trotzdem nicht, und der
                                  // Namens-Link der Folgezeile behaelt seinen
                                  // Streifen. Den Ueberstand deckt die
                                  // **Zeilenkante** (ab ihr malt die Zelle der
                                  // Folgezeile darueber), nicht die Band-Klemme —
                                  // deren Padding-Box bei ≈44 px liegt
                                  // (abgeleitet, s. `tokens.js`).
                                  //
                                  // ⚠️ **Der Boden wirkt nur mit
                                  // `MARKE_BAND_KLEMME_SX` am Band** — dessen
                                  // `overflow: hidden` schnitte den `::after`
                                  // sonst auf die Textzeile ab.
                                  //
                                  // 🔴 **Nachbesserung 7 (2026-10-16):
                                  // `flexShrink: 0`.** Der Link ist das
                                  // BEDIENELEMENT des Bandes; er gibt dem
                                  // Vorderteil keinen Pixel ab, der Vorderteil
                                  // kuerzt sich (`MARKE_BAND_VORDERTEIL_SX`).
                                  // Ohne diese Zeile schrumpfte der Link mit,
                                  // und der Norm-Boden endete wieder an der
                                  // Bandkante — genau der Befund, den M2 (b5)
                                  // rot faerbt.
                                  //
                                  // 🔴 **Nachbesserung 8 (2026-10-16): der `sx`
                                  // kommt aus `MARKE_BAND_LINK_SX`** — derselbe
                                  // Norm-Boden, aber an der RECHTEN Kante
                                  // verankert (`left: auto; right: 0`), damit er
                                  // nach LINKS in das Band waechst statt an
                                  // dessen Kante geklippt zu werden (R15 W-2 =
                                  // R16 W1 der Runde 5) — **ueber die volle
                                  // Linkbreite** (`width: '100%'`, Nachbesserung 9:
                                  // ohne sie ist der Kasten shrink-to-fit, also
                                  // genau 24 px rechts). Begruendung, Herleitung
                                  // und der ausdrueckliche Spread stehen dort.
                                  //
                                  // 🔴 **Und der Text bekommt einen inneren
                                  // Traeger** (`MARKE_BAND_KUERZUNG_SX`): die
                                  // Kuerzungs-Hausform darf NICHT auf den Link —
                                  // ihr `overflow: hidden` schnitte den
                                  // `::after`-Boden auf die Padding-Box ab (CSS
                                  // 2.1 §11.1.1). Der Link bleibt direktes Kind
                                  // des Bandes.
                                  sx={MARKE_BAND_LINK_SX}
                                  // 🔴 **Der `title` traegt den vollen Markennamen**
                                  // (Hausform der gekuerzten Texte), weil die Marke
                                  // sich seit der Nachbesserung 8 selbst kuerzen kann.
                                  // 🔴 **Die zwei Kanaele der Marken-Kuerzung, benannt
                                  // (R16 W1 der Runde 6, 2026-10-16):** der
                                  // **Link-Zweig** (mit `vendor_id`, hier) ist
                                  // fokussierbar; der `title` erreicht die MAUS, und
                                  // ein Tipp fuehrt auf die Lieferantenseite — dort
                                  // steht der volle Name. Der **Text-Zweig** (ohne
                                  // `vendor_id`, unten) ist NICHT fokussierbar: dort
                                  // erreicht `title` nur die Maus, und der
                                  // Zeilenklick fuehrt aufs PRODUKTDETAIL. **Touch
                                  // kennt in BEIDEN Zweigen kein Hovern.** Es geht
                                  // nichts verloren — der volle Name steht im DOM
                                  // (ein Screenreader liest ihn ganz), und der
                                  // Ersatzweg ist der Klick, nicht der Tooltip.
                                  title={r.brand}
                                >
                                  <Box component="span" sx={MARKE_BAND_KUERZUNG_SX}>{r.brand}</Box>
                                </Link>
                              ) : (
                                <Box
                                  component="span"
                                  // Hausform der gekuerzten Texte: `title` am
                                  // geklemmten Element (hier fallen Deckel und
                                  // Ellipse auf DASSELBE Glied — es gibt keinen
                                  // Boden, der einen inneren Traeger erzwaenge).
                                  title={r.brand}
                                  sx={{ ...MARKE_BAND_MARKE_SX, ...MARKE_BAND_KUERZUNG_SX }}
                                >
                                  {r.brand}
                                </Box>
                              )}
                            </>
                          )}
                        </Typography>
                        </Box>
                        </Box>
                      </TableCell>
                      )}
                      {/* 🔴 **G1c-2 (§2.3): die Spalte „Brand" — die MARKE
                          (`shp_products.vendor`, mit Rueckfall auf den
                          Lieferanten-Anzeigenamen), NICHT der Lieferant.**
                          Sie steht direkt hinter `Product` (Identitaetsaussage
                          wie der Name) und VOR `Category` (eine Rechengroesse).
                          🔴 Die Reihenfolge steht in DREI Stellen, die alle
                          ziehen: `SPALTEN`, `head` und hier.
                          🔴 **Link nur mit `vendor_id`** (`/vendors/<id>`,
                          Hausform `ProductDetail.jsx:194-199`); `onClick`
                          `stopPropagation` ist PFLICHT — die Zeile ist klickbar
                          (`openRow`), ohne das navigiert der Klick zweimal.
                          🔴 **Ohne `vendor_id`: Text ohne Affordanz**, kein Link
                          und KEIN `—` — der Wert IST bekannt (der Freitext), ein
                          `—` waere eine Falschaussage, und eine erfundene
                          Verknuepfung waere schlimmer (§1.2). Betrifft 2 Zeilen
                          (M-7).
                          🔴 **Leer** (kein Freitext UND kein Lieferant): `—`
                          (Hausform `ProductDetail.jsx:148`). */}
                      {sichtbar.has('brand') && (
                      <TableCell>
                        {r.brand ? (
                          r.vendor_id ? (
                            <Tooltip title="Open vendor page" describeChild>
                              <Link
                                component={RouterLink}
                                to={`/vendors/${r.vendor_id}`}
                                underline="hover"
                                color="inherit"
                                onClick={(e) => e.stopPropagation()}
                                // 🔴 **Der zugängliche Name ist die MARKE, nicht
                                // der Tooltip — und `describeChild` ist der
                                // Hausweg dorthin (R16 W3 = R15 W-4).**
                                // Gemessen: MUI setzt an einem Kind OHNE eigenes
                                // `aria-label` den Tooltip-TEXT als `aria-label`;
                                // die Zelle hiesse dann „Open vendor page", und
                                // der Bediener erfuehre NICHT, welcher Lieferant
                                // gemeint ist. Bis zur Nachbesserung 3 stand hier
                                // deshalb ein `aria-label={r.brand}` — es rettete
                                // den NAMEN, liess aber den Tooltip-TEXT
                                // ungesprochen (er wurde von `aria-label`
                                // verdraengt). `describeChild` macht den
                                // sichtbaren Text zum Namen und den Tooltip zur
                                // BESCHREIBUNG (`aria-describedby`) — beides
                                // kommt an, und der Name haengt nicht mehr an der
                                // Reihenfolge, in der MUI Kind- und
                                // Tooltip-Props spreizt (`@mui/material` 7.3.5,
                                // `Tooltip/Tooltip.js:544-556`; Hausnotiz
                                // `pattern-library.md:58-67`).
                                //
                                // 🔴 **Der NORM-Boden 24 (R16 W3 der Runde 3,
                                // Nachbesserung 5, 2026-10-16).** Der Link der
                                // Brand-Spalte ist ein BLOCK-Link in einer eigenen
                                // Zeile; die 2.5.8-Ausnahme „Inline" traegt hier
                                // nicht (er steht nicht in einem Satz), und die
                                // Abstands-Ausnahme auch nicht — die klickbare
                                // Zeile darunter ist ein ZWEITES Ziel
                                // (`openRow`), die 24-px-Kreise schnitten sich.
                                // Deshalb derselbe Boden wie am Band-2-Link, aus
                                // demselben Token.
                                sx={{ display: 'block', ...DICHTER_TEXT_LINK_SX }}
                              >
                                {/* 🔴 **KEIN `title` — genau EINE Sprechblase
                                    (R16 B2 = R15 W-1, Nachbesserung 4,
                                    2026-10-15).**
                                    Bis dahin stand hier `title={r.brand}` (die
                                    R16-W4-Abhilfe: der volle Name bei
                                    Kuerzung). Gemessen in jsdom (die Messung
                                    steht im Bericht; `@mui/material` 7.3.5):

                                    | | Ruhezustand | offen |
                                    |---|---|---|
                                    | `Link` (MUI, `describeChild`) | `title="Open vendor page"` | **`title` WEG**, `aria-describedby` da |
                                    | innerer `span` (unser `title`) | `title="<Marke>"` | unveraendert |

                                    MUI **entfernt sein eigenes `title` beim
                                    Oeffnen** — damit war der `title` am inneren
                                    `span` der EINZIGE verbleibende native
                                    Tooltip, und beim Hovern standen zwei
                                    verschiedene Texte nebeneinander („timemore"
                                    nativ, „Open vendor page" als MUI-Popup).
                                    Er faellt deshalb weg; uebrig bleibt das
                                    MUI-Popup, und das ist EIN Text.

                                    🔴 **Was damit entfaellt, und warum das
                                    vertretbar ist:** der volle Markenname im
                                    `title` bei Kuerzung. Die Zelle ist auf
                                    `COLUMN_MAX.text.max` = **320 px** gedeckelt
                                    (`tokens.js`), und Markennamen sind kurz —
                                    die Klemme greift praktisch nicht. Der
                                    zugaengliche Name des Links ist die Marke
                                    ohnehin VOLLSTAENDIG (er kommt aus dem
                                    Textinhalt, nicht aus dem gekuerzten Bild).

                                    🔴 **Gemessen, nicht geschaetzt (R16 W4,
                                    Orchestrator, streng lesend gegen prod,
                                    2026-10-16):** `shp_products.vendor` — der
                                    laengste Wert **18** Zeichen („Brewout Coffee
                                    Co."), 95-%-Quantil **11**, **43**
                                    verschiedene, **keiner ueber 24**. Bei 320 px
                                    Zelle und ~10,5–12 px Schrift passt das
                                    ohne Klemme.

                                    ⚠️ **Die Marke faellt auf zwei weitere
                                    Spalten zurueck, und die sind NICHT
                                    mitgemessen.** Die Quelle ist
                                    `COALESCE(NULLIF(BTRIM(p.vendor), ''),
                                    v.metaobject_name, v.vendor_name)`
                                    (`backend/routes/products_api.py:489`) —
                                    also `shp_products.vendor`, sonst
                                    `shp_vendors.metaobject_name`, sonst
                                    `shp_vendors.vendor_name`. Die 18 Zeichen
                                    gelten fuer die ERSTE; die zwei Rueckfall-
                                    spalten hat niemand gemessen. Wer die Klemme
                                    an einer gekuerzten Marke sieht, hat hier
                                    die Stelle, an der die Zahl fehlt.

                                    🔴 **Der `title` am `Link` selbst waere die
                                    falsche Abhilfe:** er gewaenne gegen MUI
                                    (`...children.props` steht in
                                    `Tooltip.js:544-556` NACH `nameOrDescProps`)
                                    — dann stuende „timemore" nativ NEBEN
                                    „Open vendor page" im Popup, also wieder
                                    zwei Texte. */}
                                <Box component="span" sx={MARKE_KLEMME_SX}>
                                  {r.brand}
                                </Box>
                              </Link>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" noWrap title={r.brand} sx={{ display: 'block' }}>
                              {r.brand}
                            </Typography>
                          )
                        ) : '—'}
                      </TableCell>
                      )}
                      {sichtbar.has('category_abcd') && (
                      <TableCell>
                        {(() => {
                          const cat = (r.category_abcd || '').toUpperCase();
                          const color = cat === 'A' ? 'success' : cat === 'B' ? 'info' : cat === 'C' ? 'warning' : 'default';
                          return cat ? (
                            <Chip size="small" label={cat} color={color} variant={color === 'default' ? 'outlined' : 'filled'} />
                          ) : '—';
                        })()}
                      </TableCell>
                      )}
                      {/* 🔴 Die Vertical-SPALTE ist am 2026-11-04 entfallen;
                          den Wert traegt die Ausnahme-Marke in der Namenszelle
                          (`VerticalMark`, oben). */}
                      {/* On hand traegt Incoming als Klammer — die mobile Karte
                          schreibt seit immer `Stock: 33 (+6)`, und zwei Spalten
                          fuer eine Entscheidung sind eine zu viel. */}
                      {sichtbar.has('current_inventory') && (
                      <TableCell align="right">
                        {r.current_inventory}
                        {r.incoming_inventory > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            (+{r.incoming_inventory})
                          </Typography>
                        )}
                      </TableCell>
                      )}
                      {/* Stock-out traegt den Reorder-Termin als Unterzeile und
                          wird ROT, sobald es knapp wird. Die 14-Tage-Rechnung
                          gab es im Code schon (`stockoutSoon`, mobil) — auf dem
                          Desktop war sie nur nie gezeichnet. */}
                      {sichtbar.has('stock_out_date') && (
                      <TableCell>
                        {stockoutDate && isFinite(stockoutDate) ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{ color: stockoutSoon ? 'error.main' : 'text.primary' }}
                            >
                              {formatDate(stockoutDate)}
                            </Typography>
                            {reorderDate && isFinite(reorderDate) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Reorder {formatDate(reorderDate)}
                              </Typography>
                            )}
                          </>
                        ) : '—'}
                      </TableCell>
                      )}
                      {sichtbar.has('list_price') && (
                      <TableCell align="right">{fmtCurrency(r.list_price)}</TableCell>
                      )}
                      {/* Margin wird farbig — dieselbe Skala, die die mobile
                          Karte schon hatte (`marginColor`). */}
                      {sichtbar.has('margin_pct') && (
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: marginColor }}>
                          {formatPercent(marginPct / 100, { digits: 1 })}
                        </Typography>
                      </TableCell>
                      )}
                      {sichtbar.has('average_sales_30d') && (
                      <TableCell align="right">{formatNumber(r.average_sales_30d || 0)}</TableCell>
                      )}
                      {/* Price reco — der gruene 16-px-Haken ist weg. Er trug ein
                          Bit; Betrag und Markt standen im Speicher und wurden
                          verworfen (§4.4). */}
                      {sichtbar.has('rec_abs_diff') && (
                      <TableCell align="right">
                        <Pill tone={reco.tone}>{reco.label}</Pill>
                      </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {sortedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={sichtbarerKopf.length}>{emptyState}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableViewport>
          </Box>
        )}

        {/* 🔴 `liste-fuss` — §15-Konvention, von Hand gesetzt (G2-Grill B2).
            Die Zone ist der Träger; der Baustein gibt sich bei `count = 0` gar
            nicht aus (`TableFooterSimple.jsx:38`) — deshalb steht der Behälter
            hier NUR, wenn er etwas zeigt.
            🔴 N8.7 (Nachbesserung G1a, 2026-10-14, R16 B5): „Kein leerer
            Fußzeilen-Behälter: die Fußzeile steht nur mit Inhalt. `liste-fuss`
            existiert nicht ODER hat Textinhalt." Bis hierher war der Behälter
            UNBEDINGT gerendert und stand im Leer-/Fehlerzustand leer im Baum
            (die Zone ist in G1a neu — die Bestandsliste hatte keine Marken). */}
        {sortedRows.length > 0 && (
        <Box data-testid="liste-fuss">
          <TableFooterSimple
            count={sortedRows.length}
            // 🔴 MUI zählt die Seite 0-basiert, die Adresse 1-basiert (§3.1).
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            onPageChange={(p) => set('page', p + 1)}
            // 🔴 §1.2/§4.2: **kein `leftSlot` mehr.** Der zweite Zähler
            // („Products: N") war einer von DREI Zählorten; es gibt genau EINEN
            // (`ResultCount` in Z1 bzw. Z3). `TableFooterSimple`s JSDoc sagt es
            // selbst: „leftSlot nur für Summen" — eine Summe ist das nicht.
            maxWidth="100%"
            // 🔴 F5-1 (UI-Durchgang 2 Teil 5, 2026-10-09): mobil steht die
            // Blätterleiste im FLUSS statt zu schweben — die schwebende Fassung
            // deckt die Zone [96, 140] über der Viewport-Unterkante zu, und dort
            // liegt auf `/products` die Werkzeugleiste bzw. das Listenende.
            // Bauform und Begründung: `common/TableFooterSimple.jsx` (Teil 4).
            // 🔴 BS17 der Karte (§2.1): **bestätigen, nicht hinzufügen** — die
            // Zeile stand hier schon (`:1212` vor G1a). Der Gegenfall ist G2
            // (`POOverview.jsx` ruft ohne die Prop, Vorgabe `true`).
            stickyOnMobile={false}
          />
        </Box>
        )}
        </ListToolbar>
      </Panel>

      <Dialog open={categoryInfoOpen} onClose={closeCategoryInfo} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            Category (A–D): Calculation Details
            <IconButton onClick={closeCategoryInfo} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Window: <b>{CATEGORY_DETAILS.window}</b>
              </Typography>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Inputs</Typography>
                <List dense sx={{ pl: 2 }}>
                  {CATEGORY_DETAILS.metrics.map((m) => (
                    <ListItem key={m.label} disableGutters sx={{ py: 0.25 }}>
                      <ListItemText
                        primaryTypographyProps={{ variant: 'body2' }}
                        primary={<><b>{m.label}:</b>&nbsp;{m.value}</>}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Shares</Typography>
                <List dense sx={{ pl: 2 }}>
                  {CATEGORY_DETAILS.shares.map((s) => (
                    <ListItem key={s.label} disableGutters sx={{ py: 0.25 }}>
                      <ListItemText
                        primaryTypographyProps={{ variant: 'body2', sx: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } }}
                        primary={`${s.label} = ${s.value}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Weighted score</Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0, p: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    fontSize: 12,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace'
                  }}
                >
                  {CATEGORY_DETAILS.combined}
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Weights: <b>{Math.round(CATEGORY_DETAILS.weights.revenue * 100)}% revenue</b>,&nbsp;
                  <b>{Math.round(CATEGORY_DETAILS.weights.margin * 100)}% margin</b>.
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Ranking & cumulative share</Typography>
                <Typography variant="body2">{CATEGORY_DETAILS.rank}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Cutoffs</Typography>
                <Grid container spacing={1} sx={{ fontSize: 13 }}>
                  {CATEGORY_DETAILS.buckets.map((b) => (
                    <Grid size={12} key={b.k}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip size="small" label={b.k} />
                        <Typography variant="body2"><b>{b.range}</b> — {b.note}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider />
              <Typography variant="body2" color="text.secondary">
                {CATEGORY_DETAILS.persistence}
              </Typography>
            </Stack>
          </DialogContent>
        </Dialog>

        <SlidePanel
          open={!!bundlePanel}
          onClose={closeBundlePanel}
          title={bundlePanel?.productName || 'Bundle components'}
          subtitle="Bundle components"
        >
          {bundlePanel && (
            <BundleComponentsContent
              productId={bundlePanel.productId}
              bundleSku={bundlePanel.sku}
            />
          )}
        </SlidePanel>
    </Box>
  );
}
